import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useUserData } from '../context/UserDataContext';
import { usePersistentState } from '../lib/usePersistentState';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Link2, RefreshCw, Info, Zap, X, Printer, Download, Plus, Pencil, Trash2 } from 'lucide-react';
import AccountLinkModal, { ConnectionBadge } from './AccountLinkModal';
import InvestmentProjection from './InvestmentProjection';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt   = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtC  = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, notation: 'compact' }).format(n);
// eslint-disable-next-line no-unused-vars
const fmtPct = (n, d = 1) => { const v = n ?? 0; return `${v >= 0 ? '+' : ''}${Number(v).toFixed(d)}%`; };

// ─── Asset class colours ──────────────────────────────────────────────────────
const ASSET_COLORS = {
  'Large Cap US':  '#3b82f6',
  'International': '#8b5cf6',
  'Mid/Small Cap': '#06b6d4',
  'Bonds':         '#10b981',
  'REITs':         '#f59e0b',
  'Alternatives':  '#f97316',
  'Sector':        '#ec4899',
  'Cash':          '#64748b',
};

const ACCOUNT_TYPE_LABELS = {
  '401k':        '401(k)',
  'roth_ira':    'Roth IRA',
  'ira':         'IRA',
  'taxable':     'Taxable',
  'brokerage':   'Brokerage',
  'hysa':        'HYSA',
  'checking':    'Checking',
  'crypto':      'Crypto',
  'real_estate': 'Real Estate',
  'other':       'Other',
};

// ─── Ticker → asset-class inference ──────────────────────────────────────────
// Used to auto-fill asset class when user enters a known ticker in manual holdings.
const TICKER_ASSET_CLASS = {
  // US Large Cap ETFs & individual mega-caps
  VOO:'Large Cap US', VTI:'Large Cap US', SPY:'Large Cap US', IVV:'Large Cap US',
  QQQ:'Large Cap US', SCHB:'Large Cap US', SCHX:'Large Cap US', VV:'Large Cap US',
  AAPL:'Large Cap US', MSFT:'Large Cap US', AMZN:'Large Cap US', GOOGL:'Large Cap US',
  GOOG:'Large Cap US', META:'Large Cap US', NVDA:'Large Cap US', TSLA:'Large Cap US',
  JPM:'Large Cap US', JNJ:'Large Cap US', V:'Large Cap US', WMT:'Large Cap US',
  PG:'Large Cap US', UNH:'Large Cap US', MA:'Large Cap US', HD:'Large Cap US',
  // Mid / Small Cap
  VXF:'Mid/Small Cap', IJH:'Mid/Small Cap', IJR:'Mid/Small Cap', VO:'Mid/Small Cap',
  VB:'Mid/Small Cap', SCHA:'Mid/Small Cap', VBR:'Mid/Small Cap', VBK:'Mid/Small Cap',
  // International
  VXUS:'International', VEA:'International', VWO:'International', EFA:'International',
  IEFA:'International', IEMG:'International', VT:'International', ACWI:'International',
  EEM:'International', SPDW:'International', TSM:'International',
  // Bonds
  BND:'Bonds', AGG:'Bonds', TLT:'Bonds', SHY:'Bonds', IEF:'Bonds',
  BNDX:'Bonds', LQD:'Bonds', HYG:'Bonds', BIV:'Bonds', BSV:'Bonds',
  VTIP:'Bonds', MUB:'Bonds', VBTLX:'Bonds',
  // REITs
  VNQ:'REITs', SCHH:'REITs', IYR:'REITs', O:'REITs', AMT:'REITs',
  PLD:'REITs', EQIX:'REITs', SPG:'REITs',
  // Crypto → Alternatives (matches existing ASSET_COLORS key)
  BTC:'Alternatives', ETH:'Alternatives', SOL:'Alternatives', ADA:'Alternatives',
  DOGE:'Alternatives', DOT:'Alternatives', AVAX:'Alternatives', XRP:'Alternatives',
  // Sector ETFs
  XLK:'Sector', XLF:'Sector', XLV:'Sector', XLE:'Sector', XLI:'Sector',
  XLC:'Sector', ARKK:'Sector', VGT:'Sector', SMH:'Sector', SOXX:'Sector',
  // Cash / Money Market
  VMFXX:'Cash', SPAXX:'Cash', SGOV:'Cash', BIL:'Cash', SHV:'Cash', FDLXX:'Cash',
};

// Default asset class for each account type (fallback when ticker is unknown)
function inferAssetClassFromType(accountType) {
  const MAP = {
    brokerage:   'Large Cap US',
    '401k':      'Large Cap US',
    ira:         'Large Cap US',
    roth_ira:    'Large Cap US',
    crypto:      'Alternatives',
    real_estate: 'REITs',
    other:       'Large Cap US',
  };
  return MAP[accountType] || 'Large Cap US';
}

// Account type options for the manual entry modal
const ACCOUNT_TYPE_OPTIONS = [
  { id: 'brokerage',   label: 'Brokerage',  icon: '📈' },
  { id: '401k',        label: '401(k)',      icon: '🏢' },
  { id: 'ira',         label: 'IRA',         icon: '🏦' },
  { id: 'roth_ira',    label: 'Roth IRA',   icon: '🔵' },
  { id: 'crypto',      label: 'Crypto',      icon: '₿'  },
  { id: 'real_estate', label: 'Real Estate', icon: '🏠' },
  { id: 'other',       label: 'Other',       icon: '💼' },
];

// ─── Asset class historical stats (annualised averages) ───────────────────────
const ASSET_CLASS_STATS = {
  'Large Cap US':  { return: 10.5, vol: 16.0, beta: 1.00 },
  'International': { return:  8.0, vol: 17.0, beta: 0.80 },
  'Mid/Small Cap': { return: 11.5, vol: 19.0, beta: 1.20 },
  'Bonds':         { return:  4.0, vol:  6.0, beta: -0.10 },
  'REITs':         { return:  9.0, vol: 18.0, beta: 1.30 },
  'Alternatives':  { return:  7.0, vol: 12.0, beta: 0.50 },
  'Sector':        { return: 10.0, vol: 18.0, beta: 1.10 },
  'Cash':          { return:  3.0, vol:  1.0, beta:  0.00 },
};

// ─── State long-term capital gains rates (%) ──────────────────────────────────
const STATE_TAX_RATES = {
  'AL':5.0,'AK':0,'AZ':2.5,'AR':4.4,'CA':9.3,'CO':4.4,'CT':6.99,'DE':6.6,
  'FL':0,'GA':5.49,'HI':7.25,'ID':5.8,'IL':4.95,'IN':3.05,'IA':6.0,'KS':5.7,
  'KY':4.5,'LA':4.25,'ME':7.15,'MD':5.75,'MA':5.0,'MI':4.25,'MN':9.85,'MS':5.0,
  'MO':4.95,'MT':6.75,'NE':6.64,'NV':0,'NH':0,'NJ':10.75,'NM':5.9,'NY':10.9,
  'NC':4.75,'ND':2.5,'OH':3.75,'OK':4.75,'OR':9.9,'PA':3.07,'RI':5.99,'SC':6.5,
  'SD':0,'TN':0,'TX':0,'UT':4.65,'VT':8.75,'VA':5.75,'WA':0,'WV':6.5,'WI':7.65,
  'WY':0,'DC':10.75,
};

// ─── Portfolio statistics helper ──────────────────────────────────────────────
function computePortfolioStats(allocation, totalValue) {
  if (!allocation || allocation.length === 0 || !totalValue) return null;
  let portReturn = 0, portVolSq = 0, beta = 0;
  allocation.forEach(a => {
    const w = a.value / totalValue;
    const stats = ASSET_CLASS_STATS[a.name];
    if (!stats) return;
    portReturn += w * stats.return;
    portVolSq  += Math.pow(w * stats.vol, 2);
    beta       += w * stats.beta;
  });
  const portVol = Math.sqrt(portVolSq) * 1.3;  // 1.3x scale for inter-asset correlations
  const sharpe  = portVol > 0 ? (portReturn - 5.0) / portVol : 0;
  const var95   = totalValue * Math.max(0, (1.645 * portVol / 100) - (portReturn / 100));
  const maxDD   = portVol * 2.2;
  return { portReturn, portVol, beta, sharpe, var95, maxDD };
}

// ─── Tax estimate helper ──────────────────────────────────────────────────────
function estimateTax(dollarGain, filingStatus, annualIncome, stateAbbr) {
  const mfj     = filingStatus === 'married' || filingStatus === 'married_filing_jointly';
  const ltcg0   = mfj ? 94050  : 47025;
  const ltcg15  = mfj ? 583750 : 518900;
  const niitLim = mfj ? 250000 : 200000;
  let ltcgRate = 0;
  if (annualIncome > ltcg15) ltcgRate = 0.20;
  else if (annualIncome > ltcg0) ltcgRate = 0.15;
  const niit     = annualIncome > niitLim ? 0.038 : 0;
  const stateR   = (STATE_TAX_RATES[stateAbbr] ?? 5.0) / 100;
  const fedTax   = dollarGain * (ltcgRate + niit);
  const stateTax = dollarGain * stateR;
  return { fedTax, stateTax, totalTax: fedTax + stateTax, ltcgRate: (ltcgRate + niit) * 100, stateRate: stateR * 100 };
}

// ─── Box-Muller RNG ───────────────────────────────────────────────────────────
function boxMuller() {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

// ─── Investment Monte Carlo engine ───────────────────────────────────────────
function runInvestmentMC({ startValue, monthlyContrib, annualReturn, annualVol, years, numSims = 500 }) {
  const μ = (annualReturn / 100) / 12;
  const σ = (annualVol / 100) / Math.sqrt(12);
  const paths = [];

  for (let s = 0; s < numSims; s++) {
    let val = startValue;
    const path = [val];
    for (let m = 1; m <= years * 12; m++) {
      const ret = μ - (σ * σ) / 2 + σ * boxMuller();
      val = val * Math.exp(ret) + monthlyContrib;
      if (m % 12 === 0) path.push(Math.max(val, 0));
    }
    paths.push(path);
  }

  // Percentiles at each year
  const percentiles = [];
  for (let y = 0; y <= years; y++) {
    const vals = paths.map(p => p[y]).sort((a, b) => a - b);
    const q = (p) => vals[Math.floor(numSims * p)];
    percentiles.push({ year: y, p10: q(0.10), p25: q(0.25), p50: q(0.50), p75: q(0.75), p90: q(0.90) });
  }
  return { paths, percentiles, numSims };
}

// ─── Monte Carlo canvas ───────────────────────────────────────────────────────
function InvestmentCanvas({ result, resultReal, years }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!result || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const PAD = { top: 20, right: 20, bottom: 40, left: 70 };
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;

    ctx.clearRect(0, 0, W, H);

    // Max value across both nominal and real paths
    let maxVal = 0;
    result.paths.forEach(p => p.forEach(v => { if (v > maxVal) maxVal = v; }));
    maxVal *= 1.05;

    const xScale = (yr) => PAD.left + (yr / years) * plotW;
    const yScale = (v)  => PAD.top  + plotH - (v / maxVal) * plotH;

    // Grid
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = PAD.top + (plotH / 4) * i;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(W - PAD.right, y); ctx.stroke();
    }

    // Nominal paths (faint)
    result.paths.forEach(path => {
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(59,130,246,0.05)';
      ctx.lineWidth = 1;
      path.forEach((v, i) => {
        const x = xScale(i), y = yScale(v);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
    });

    // Shaded band p25–p75 (nominal only)
    const { percentiles } = result;
    ctx.beginPath();
    percentiles.forEach((pt, i) => {
      const x = xScale(pt.year), y = yScale(pt.p75);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    [...percentiles].reverse().forEach(pt => ctx.lineTo(xScale(pt.year), yScale(pt.p25)));
    ctx.closePath();
    ctx.fillStyle = 'rgba(59,130,246,0.12)';
    ctx.fill();

    // Shaded band p10–p90 (nominal, lighter)
    ctx.beginPath();
    percentiles.forEach((pt, i) => {
      const x = xScale(pt.year), y = yScale(pt.p90);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    [...percentiles].reverse().forEach(pt => ctx.lineTo(xScale(pt.year), yScale(pt.p10)));
    ctx.closePath();
    ctx.fillStyle = 'rgba(59,130,246,0.05)';
    ctx.fill();

    // Nominal median line (blue)
    ctx.beginPath();
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2.5;
    percentiles.forEach((pt, i) => {
      const x = xScale(pt.year), y = yScale(pt.p50);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Real median line (green) — drawn on top
    if (resultReal) {
      ctx.beginPath();
      ctx.strokeStyle = '#34d399';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      resultReal.percentiles.forEach((pt, i) => {
        const x = xScale(pt.year), y = yScale(pt.p50);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Axes
    ctx.fillStyle = '#475569';
    ctx.font = '11px system-ui';
    ctx.textAlign = 'center';
    for (let yr = 0; yr <= years; yr += Math.max(1, Math.floor(years / 5))) {
      const x = xScale(yr);
      ctx.fillText(`Yr ${yr}`, x, H - PAD.bottom + 16);
    }
    ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
      const v = (maxVal / 4) * (4 - i);
      const y = PAD.top + (plotH / 4) * i;
      ctx.fillText(fmtC(v), PAD.left - 6, y + 4);
    }
  }, [result, resultReal, years]);

  return (
    <canvas
      ref={canvasRef}
      width={700} height={280}
      className="w-full h-full rounded-lg"
      style={{ background: '#0f172a' }}
    />
  );
}

// ─── Slider ───────────────────────────────────────────────────────────────────
function Slider({ label, value, min, max, step, onChange, fmt: fmtFn, sub }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-slate-400 font-medium">{label}</span>
        <span className="text-sm font-bold text-slate-100">{fmtFn ? fmtFn(value) : value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none bg-slate-700 accent-blue-500 cursor-pointer"
      />
      {sub && <div className="text-xs text-slate-600 mt-1">{sub}</div>}
    </div>
  );
}

// ─── Plain-English tooltip definitions ───────────────────────────────────────
const TOOLTIPS = {
  monteCarlo:        'A method that runs 1,000 random scenarios using real historical data to show you the full range of possible outcomes for your portfolio — from the best years the market has seen to the worst.',
  valueAtRisk:       'The most you could expect to lose in a bad year (with 95% confidence). If your 1-year VaR is $5,000, that means in 19 out of 20 years your losses would not exceed $5,000.',
  sharpeRatio:       'Measures how much return you\'re getting for the risk you\'re taking. Higher is better — above 1.0 is good, above 2.0 is excellent. A Sharpe of 0.5 means you\'re being compensated only modestly for the risk.',
  beta:              'How much your portfolio moves compared to the overall stock market. A beta of 1.0 means it moves in lockstep with the S&P 500. Above 1 means more volatile; below 1 means steadier.',
  maxDrawdown:       'The biggest estimated drop from a peak to a low point your portfolio could experience. Think of it as the worst-case scenario you might have to live through before markets recover.',
  standardDeviation: 'How much your annual returns are expected to bounce around. A higher number means more ups and downs. Lower means a steadier, more predictable ride.',
  annualizedReturn:  'The expected average yearly return based on the weighted historical averages of each asset class in your portfolio. This is an estimate, not a guarantee.',
  volatility:        'How much your portfolio\'s value is expected to fluctuate year to year. Higher volatility means bigger swings in both directions. The S&P 500 historically averages about 15–17% annual volatility.',
  percentile:        'Percentiles rank all 1,000 simulation outcomes. P10 (10th percentile) means 90% of outcomes were better — it\'s the "bad but not the worst" scenario. P90 means 90% of outcomes were worse — the best-case scenario.',
};

// ─── Reusable info-tooltip component ─────────────────────────────────────────
function InfoTooltip({ text, side = 'top' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <span className="relative inline-flex items-center" ref={ref}>
      <button
        type="button"
        className="text-slate-500 hover:text-blue-400 transition-colors flex-shrink-0 ml-1 align-middle focus:outline-none"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-label="More information"
      >
        <Info size={12} />
      </button>
      {open && (
        <span
          className={`absolute z-[60] w-64 p-3 bg-slate-700 border border-slate-600 rounded-xl shadow-2xl text-xs text-slate-200 leading-relaxed pointer-events-none
            ${side === 'bottom' ? 'top-full left-1/2 -translate-x-1/2 mt-2' : 'bottom-full left-1/2 -translate-x-1/2 mb-2'}`}
          style={{ whiteSpace: 'normal', minWidth: '220px' }}
        >
          {text}
          {/* Arrow */}
          <span style={{
            position: 'absolute',
            ...(side === 'bottom'
              ? { bottom: '100%', left: '50%', transform: 'translateX(-50%) translateY(50%) rotate(45deg)', borderTop: '1px solid #475569', borderLeft: '1px solid #475569' }
              : { top: '100%',    left: '50%', transform: 'translateX(-50%) translateY(-50%) rotate(45deg)', borderRight: '1px solid #475569', borderBottom: '1px solid #475569' }
            ),
            width: 8, height: 8, background: '#334155',
          }} />
        </span>
      )}
    </span>
  );
}

// ─── Report generators (open print-ready HTML in a new window) ───────────────
function generateSimulatorReport({ startValue, monthlyAdd, years, expReturn, vol, result, userName }) {
  const final  = result?.percentiles[result.percentiles.length - 1];
  const date   = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const p5rows = final
    ? [
        { label: 'P10 — Bear', val: final.p10, col: '#dc2626', note: 'Worst 10%' },
        { label: 'P25',        val: final.p25, col: '#d97706', note: 'Below avg' },
        { label: 'P50 — Median',val:final.p50, col: '#2563eb', note: 'Most likely' },
        { label: 'P75',        val: final.p75, col: '#16a34a', note: 'Above avg' },
        { label: 'P90 — Bull', val: final.p90, col: '#15803d', note: 'Top 10%' },
      ].map(r => `<div class="pcard" style="border-top:3px solid ${r.col}"><div class="plabel" style="color:${r.col}">${r.label}</div><div class="pval" style="color:${r.col}">${fmtC(r.val)}</div><div class="pnote">${r.note}</div></div>`).join('')
    : '';
  const totalContrib = fmt(monthlyAdd * 12 * years);
  const gain = final ? final.p50 - startValue : 0;

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Finovo — Monte Carlo Report</title><style>
*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1e293b;padding:48px;font-size:13px;line-height:1.6}
.hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #e2e8f0}
.logo{width:36px;height:36px;background:#2563eb;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:17px;flex-shrink:0}
.brand{font-size:20px;font-weight:700;color:#1e293b}.brandsub{font-size:11px;color:#94a3b8}
.meta{text-align:right;font-size:12px;color:#64748b}.muser{font-weight:600;color:#1e293b;font-size:14px}
.stitle{font-size:15px;font-weight:700;color:#1e293b;margin:28px 0 12px;padding-bottom:8px;border-bottom:1px solid #e2e8f0}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px}
.g4{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:12px;margin-bottom:16px}
.card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px}
.clabel{font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.05em;font-weight:600;margin-bottom:4px}
.cval{font-size:22px;font-weight:700}
.pcards{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:20px}
.pcard{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px;text-align:center}
.plabel{font-size:11px;font-weight:600;margin-bottom:6px}
.pval{font-size:18px;font-weight:700}
.pnote{font-size:10px;color:#94a3b8;margin-top:4px}
.sbox{background:#f0f9ff;border:1px solid #bae6fd;border-radius:10px;padding:16px;margin-bottom:20px}
.sbox p{color:#0c4a6e;margin-bottom:10px}
.sitem{display:flex;align-items:flex-start;gap:10px;margin-bottom:8px;padding:10px 12px;border-radius:8px}
.sitem.bull{background:#f0fdf4;border:1px solid #bbf7d0}.sitem.mid{background:#eff6ff;border:1px solid #bfdbfe}.sitem.bear{background:#fff1f2;border:1px solid #fecdd3}
.arr{font-size:16px;font-weight:700;flex-shrink:0;margin-top:1px}
.dis{margin-top:32px;padding:14px 16px;background:#fffbeb;border:1px solid #fde68a;border-radius:10px;font-size:11px;color:#78350f;line-height:1.6}
@media print{body{padding:24px}}</style></head><body>
<div class="hdr"><div style="display:flex;align-items:center;gap:10px"><div class="logo">F</div><div><div class="brand">Finovo</div><div class="brandsub">Personal Finance Dashboard</div></div></div>
<div class="meta"><div class="muser">${userName || 'Finovo User'}</div><div>${date}</div><div style="margin-top:2px;font-size:11px;color:#94a3b8">Monte Carlo Simulation Report</div></div></div>

<div class="stitle">Simulation Inputs</div>
<div class="g4">
<div class="card"><div class="clabel">Starting Value</div><div class="cval" style="color:#2563eb">${fmt(startValue)}</div></div>
<div class="card"><div class="clabel">Monthly Contribution</div><div class="cval" style="color:#2563eb">${fmt(monthlyAdd)}</div></div>
<div class="card"><div class="clabel">Time Horizon</div><div class="cval">${years} years</div></div>
<div class="card"><div class="clabel">Total Contributions</div><div class="cval">${totalContrib}</div></div>
</div>
<div class="g2">
<div class="card"><div class="clabel">Expected Annual Return</div><div class="cval" style="color:#059669">${expReturn}%</div></div>
<div class="card"><div class="clabel">Annual Volatility</div><div class="cval" style="color:#d97706">${vol}%</div></div>
</div>

${final ? `<div class="stitle">Results After ${years} Years (1,000 Simulations)</div>
<div class="pcards">${p5rows}</div>
<div class="stitle">Plain-English Summary</div>
<div class="sbox">
<p>Based on 1,000 simulations, if you keep investing this way for <strong>${years} years</strong>:</p>
<div class="sitem bull"><div class="arr" style="color:#16a34a">↑</div><div>In a <strong>great scenario (top 10%)</strong>, your portfolio could grow to <strong style="color:#16a34a">${fmtC(final.p90)}</strong></div></div>
<div class="sitem mid"><div class="arr" style="color:#2563eb">→</div><div>In a <strong>typical scenario (middle)</strong>, your portfolio would be around <strong style="color:#2563eb">${fmtC(final.p50)}</strong></div></div>
<div class="sitem bear"><div class="arr" style="color:#dc2626">↓</div><div>In a <strong>tough scenario (bottom 10%)</strong>, your portfolio might be <strong style="color:#dc2626">${fmtC(final.p10)}</strong></div></div>
${gain > 0 ? `<p style="margin-top:10px;font-size:12px;color:#374151">In the median case, your money would grow by <strong style="color:#059669">${fmtC(gain)}</strong> — a <strong style="color:#059669">${((final.p50 / startValue - 1) * 100).toFixed(0)}% total gain</strong>.</p>` : ''}
</div>` : ''}

<div class="dis">⚠ <strong>Disclaimer:</strong> This report is for informational purposes only and does not constitute financial advice. Monte Carlo uses log-normal return distributions with ${expReturn}% expected annual return and ${vol}% annual volatility. All figures are nominal (not inflation-adjusted). Past performance does not guarantee future results. Consult a licensed financial advisor before making investment decisions.</div>
</body></html>`;

  const w = window.open('', '_blank', 'width=920,height=750,scrollbars=yes');
  if (!w) { alert('Please allow pop-ups to generate the report.'); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}

function generateRiskReport({ profile, score, mcResult, totalValue, rebalActions, userName }) {
  const final = mcResult?.percentiles[mcResult.percentiles.length - 1];
  const date  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const recAlloc = profile.allocation;

  const allocRows = Object.entries(recAlloc)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([name, pct]) => `<tr><td>${name}</td><td style="text-align:right;font-weight:600">${pct}%</td><td style="text-align:right">${fmt(totalValue * pct / 100)}</td></tr>`)
    .join('');

  const rebalRows = (rebalActions || [])
    .map(a => `<tr><td>${a.cls}</td><td style="text-align:right">${a.cur.toFixed(1)}%</td><td style="text-align:right;font-weight:600;color:${a.diff > 0 ? '#16a34a' : '#dc2626'}">${a.rec}%</td><td style="text-align:right;font-weight:600;color:${a.diff > 0 ? '#16a34a' : '#dc2626'}">${a.diff > 0 ? '+' : '-'}${fmt(Math.abs(a.dollar))}</td></tr>`)
    .join('');

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Finovo — Risk Profile Report</title><style>
*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1e293b;padding:48px;font-size:13px;line-height:1.6}
.hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #e2e8f0}
.logo{width:36px;height:36px;background:#2563eb;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:17px;flex-shrink:0}
.brand{font-size:20px;font-weight:700}.brandsub{font-size:11px;color:#94a3b8}
.meta{text-align:right;font-size:12px;color:#64748b}.muser{font-weight:600;color:#1e293b;font-size:14px}
.stitle{font-size:15px;font-weight:700;color:#1e293b;margin:28px 0 12px;padding-bottom:8px;border-bottom:1px solid #e2e8f0}
.hero{background:linear-gradient(135deg,#eff6ff,#e0f2fe);border:1px solid #93c5fd;border-radius:12px;padding:20px;margin-bottom:20px}
.pname{font-size:26px;font-weight:800;color:#1e40af;margin-bottom:4px}
.pscore{font-size:14px;color:#3b82f6;font-weight:600;margin-bottom:8px}
.pdesc{font-size:13px;color:#374151}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px}
.g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:16px}
.card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px;text-align:center}
.clabel{font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.05em;font-weight:600;margin-bottom:4px}
.cval{font-size:20px;font-weight:700}
table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:20px}
th{text-align:left;padding:8px 12px;background:#f1f5f9;color:#475569;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.04em}
td{padding:9px 12px;border-bottom:1px solid #f1f5f9;color:#334155}
tr:last-child td{border-bottom:none}
.dis{margin-top:32px;padding:14px 16px;background:#fffbeb;border:1px solid #fde68a;border-radius:10px;font-size:11px;color:#78350f;line-height:1.6}
@media print{body{padding:24px}}</style></head><body>
<div class="hdr"><div style="display:flex;align-items:center;gap:10px"><div class="logo">F</div><div><div class="brand">Finovo</div><div class="brandsub">Personal Finance Dashboard</div></div></div>
<div class="meta"><div class="muser">${userName || 'Finovo User'}</div><div>${date}</div><div style="margin-top:2px;font-size:11px;color:#94a3b8">Risk Profile Report</div></div></div>

<div class="stitle">Your Risk Profile</div>
<div class="hero">
<div class="pname">${profile.name}</div>
<div class="pscore">Score: ${score}/32 · ${profile.tagline}</div>
<div class="pdesc">${profile.description}</div>
<div style="display:flex;gap:32px;margin-top:14px">
<div><div style="font-size:22px;font-weight:700;color:#2563eb">${profile.expectedReturn}%</div><div style="font-size:11px;color:#64748b">Expected return/yr</div></div>
<div><div style="font-size:22px;font-weight:700;color:#374151">±${profile.volatility}%</div><div style="font-size:11px;color:#64748b">Annual volatility</div></div>
${final ? `<div><div style="font-size:22px;font-weight:700;color:#2563eb">${fmtC(final.p50)}</div><div style="font-size:11px;color:#64748b">Median 20-yr value</div></div>` : ''}
</div>
</div>

<div class="stitle">Recommended Asset Allocation</div>
<table><thead><tr><th>Asset Class</th><th style="text-align:right">Target %</th><th style="text-align:right">Dollar Amount</th></tr></thead>
<tbody>${allocRows}</tbody></table>

${final ? `<div class="stitle">Monte Carlo Projection (20-Year Horizon · 500 Simulations)</div>
<div class="g3">
<div class="card"><div class="clabel" style="color:#dc2626">Bear Scenario (P10)</div><div class="cval" style="color:#dc2626">${fmtC(final.p10)}</div><div style="font-size:10px;color:#94a3b8;margin-top:4px">Worst 10% of scenarios</div></div>
<div class="card"><div class="clabel" style="color:#2563eb">Median (P50)</div><div class="cval" style="color:#2563eb">${fmtC(final.p50)}</div><div style="font-size:10px;color:#94a3b8;margin-top:4px">Most likely outcome</div></div>
<div class="card"><div class="clabel" style="color:#16a34a">Bull Scenario (P90)</div><div class="cval" style="color:#16a34a">${fmtC(final.p90)}</div><div style="font-size:10px;color:#94a3b8;margin-top:4px">Top 10% of scenarios</div></div>
</div>` : ''}

${rebalRows ? `<div class="stitle">Suggested Rebalancing Actions</div>
<table><thead><tr><th>Asset Class</th><th style="text-align:right">Current</th><th style="text-align:right">Target</th><th style="text-align:right">Action ($)</th></tr></thead>
<tbody>${rebalRows}</tbody></table>` : ''}

<div class="dis">⚠ <strong>Disclaimer:</strong> This report is for informational purposes only and does not constitute financial advice. Risk profiles are based on questionnaire responses and historical asset class averages. All figures are estimates. Past performance does not guarantee future results. Consult a licensed financial advisor before making investment decisions.</div>
</body></html>`;

  const w = window.open('', '_blank', 'width=920,height=750,scrollbars=yes');
  if (!w) { alert('Please allow pop-ups to generate the report.'); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}

// ─── Aggregation helpers ──────────────────────────────────────────────────────
// Builds the flat holdings list from investingData (mock/setup accounts)
// ─── Live price fetcher (Alpha Vantage) ───────────────────────────────────────
// Free tier: 25 calls/day — prices are cached in sessionStorage keyed by date
// so refreshing the page reuses today's cache instead of burning API quota.
const AV_CACHE_PREFIX = 'av_prices_';
const today = () => new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'

function readCache() {
  try {
    const raw = sessionStorage.getItem(AV_CACHE_PREFIX + today());
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCache(map) {
  try { sessionStorage.setItem(AV_CACHE_PREFIX + today(), JSON.stringify(map)); }
  catch { /* sessionStorage full — ignore */ }
}

// Cache stores { TICKER: { price, prevClose } }
function useLivePrices(tickers) {
  const [quotes,    setQuotes]    = useState({}); // { TICKER: { price, prevClose } }
  const [priceDate, setPriceDate] = useState(null);

  // Stable string key — effect only re-runs when the ticker set changes.
  const tickerKey = [...new Set(
    (tickers || []).filter(Boolean).map(t => t.toUpperCase())
  )].sort().join(',');

  useEffect(() => {
    console.log('[AV] useEffect fired — tickers:', tickerKey || '(none yet)');

    const key = process.env.REACT_APP_ALPHA_VANTAGE_KEY;
    if (!key) {
      console.warn('[AV] REACT_APP_ALPHA_VANTAGE_KEY missing — add it to .env and restart the dev server');
      return;
    }
    if (!tickerKey) {
      console.log('[AV] No tickers yet — will fetch once holdings are loaded');
      return;
    }

    // ── Check session cache first ────────────────────────────────────────────
    const cached = readCache(); // { TICKER: { price, prevClose } } or null
    if (cached) {
      const unique = tickerKey.split(',');
      const allCached = unique.every(t => cached[t]?.price != null);
      if (allCached) {
        console.log('[AV] All quotes served from cache:', cached);
        setQuotes(cached);
        setPriceDate(new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
        return;
      }
    }

    let cancelled = false;

    const fetchPrices = async () => {
      const unique   = tickerKey.split(',');
      const existing = cached || {};
      const toFetch  = unique.filter(t => existing[t]?.price == null);

      console.log('[AV] Tickers to fetch (not in cache):', toFetch);

      const map = { ...existing };

      for (let i = 0; i < toFetch.length; i++) {
        if (cancelled) return;

        const ticker = toFetch[i];
        const url = `https://www.alphavantage.co/query?function=GLOBAL_QUOTE&symbol=${ticker}&apikey=${key}`;
        console.log(`[AV] Fetching ${ticker} (${i + 1}/${toFetch.length}):`, url);

        try {
          const res  = await fetch(url);
          const data = await res.json();
          console.log(`[AV] Response for ${ticker}:`, data);

          const quote = data?.['Global Quote'];
          if (quote && quote['05. price']) {
            const price     = parseFloat(quote['05. price']);
            const prevClose = parseFloat(quote['08. previous close']) || null;
            if (!isNaN(price)) {
              map[ticker] = { price, prevClose };
              console.log(`[AV] Mapped: ${ticker} → price=${price}, prevClose=${prevClose}`);
            } else {
              console.warn(`[AV] ${ticker}: price not a number —`, quote['05. price']);
            }
          } else {
            console.warn(`[AV] ${ticker}: no Global Quote —`, data);
          }
        } catch (err) {
          console.error(`[AV] Fetch error for ${ticker}:`, err);
        }

        // 500 ms delay between calls to stay within rate limit
        if (i < toFetch.length - 1) await new Promise(r => setTimeout(r, 500));
      }

      if (cancelled) return;

      writeCache(map);
      setQuotes(map);
      setPriceDate(new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
      console.log('[AV] Final quote map:', map);
    };

    fetchPrices();
    return () => { cancelled = true; };
  }, [tickerKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose flat price map for backwards-compat with buildAllHoldings
  const prices = useMemo(() => {
    const m = {};
    Object.entries(quotes).forEach(([t, q]) => { if (q?.price != null) m[t] = q.price; });
    return m;
  }, [quotes]);

  return { prices, quotes, priceDate };
}

// plus any manual investment accounts stored in linkedAccountsData.
function buildAllHoldings(data, manualInvestmentAccounts = [], livePrices = {}) {
  const fromInvesting = (data?.linkedAccounts || []).flatMap(acc =>
    (acc.holdings || []).map(h => ({
      ...h,
      accountName:      acc.name,
      accountType:      acc.type,
      accountId:        acc.id,
      costBasisTotal:   h.costBasisTotal ?? null,
      costBasisPerShare: h.costBasisPerShare ?? null,
    }))
  );
  const fromManual = manualInvestmentAccounts.flatMap(acc =>
    (acc.holdings || []).map(h => {
      const upper     = (h.ticker || '').toUpperCase();
      const shares    = h.shares || 0;
      const livePrice = livePrices[upper];
      const price     = livePrice ?? h.currentPrice ?? 0;
      const cb        = h.costBasisPerShare || 0;
      return {
        ticker:            upper || h.ticker,
        name:              h.name || upper || 'Unknown',
        assetClass:        h.assetClass || TICKER_ASSET_CLASS[upper] || inferAssetClassFromType(acc.type),
        value:             shares * price,
        shares,
        currentPrice:      price,
        costBasisPerShare: cb || null,
        costBasisTotal:    h.costBasisTotal ?? (cb > 0 ? shares * cb : null),
        dayChangePct:      0,
        accountName:       acc.name,
        accountType:       acc.type,
        accountId:         acc.id,
        holdingId:         h.id || upper,
        isManual:          true,
      };
    })
  );
  return [...fromInvesting, ...fromManual];
}

function buildAllocation(allHoldings) {
  const map = {};
  allHoldings.forEach(h => {
    map[h.assetClass] = (map[h.assetClass] || 0) + h.value;
  });
  return Object.entries(map)
    .map(([name, value]) => ({ name, value, color: ASSET_COLORS[name] || '#94a3b8' }))
    .sort((a, b) => b.value - a.value);
}

// ─── Overview view ────────────────────────────────────────────────────────────
function OverviewView({ allHoldings, allocation, totalValue, investingData, manualInvestmentAccounts, combinedTotalReturn, hasCostBasis, todayGain, hasTodayGain, livePrices = {} }) {
  const { linkedAccountsData, userProfile, updateSection } = useUserData();
  const [showLinkModal,    setShowLinkModal]    = useState(false);
  const [showManualModal,  setShowManualModal]  = useState(false);
  const [editingAccount,   setEditingAccount]   = useState(null);
  const [deletingAccount,  setDeletingAccount]  = useState(null);

  const isFreePlan = userProfile?.plan === 'free';

  const INVESTMENT_TYPES = ['brokerage', '401k', 'roth_ira', 'ira', 'crypto', 'real_estate', 'other'];
  const userLinkedAccounts = (linkedAccountsData || []).filter(a => INVESTMENT_TYPES.includes(a.type));

  const handleLinkClick = () => {
    if (isFreePlan) setShowManualModal(true);
    else setShowLinkModal(true);
  };

  const handleSaveManualAccount = (account) => {
    const updated = [...(linkedAccountsData || []), account];
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
  };

  const handleEditAccount = (updatedAccount) => {
    const updated = (linkedAccountsData || []).map(a => a.id === updatedAccount.id ? updatedAccount : a);
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
  };

  const handleDeleteAccount = (accountId) => {
    const updated = (linkedAccountsData || []).filter(a => a.id !== accountId);
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
    setDeletingAccount(null);
  };

  // Only show real holdings with a live price > 0
  const topHoldings = [...allHoldings]
    .filter(h => h.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 7);

  const pieData = allocation.filter(a => a.value > 0).map(a => ({ name: a.name, value: a.value, color: a.color }));
  const totalAccounts = manualInvestmentAccounts.length;
  const hasHoldings   = allHoldings.length > 0;
  const hasValue      = totalValue > 0;

  // KPI cards — computed only from real data
  const kpis = [
    {
      label: 'Total Portfolio',
      value: hasValue ? fmt(totalValue) : (hasHoldings ? '—' : '$0'),
      sub: totalAccounts === 0
        ? 'Add accounts to get started'
        : `${totalAccounts} account${totalAccounts !== 1 ? 's' : ''} tracked`,
      color: hasValue ? 'text-slate-100' : 'text-slate-500',
    },
    {
      label: "Today's Gain",
      value: hasTodayGain ? ((todayGain >= 0 ? '+' : '') + fmt(todayGain)) : '—',
      sub: hasTodayGain
        ? 'based on previous close'
        : hasHoldings ? 'Fetching live prices…' : 'Add holdings to track',
      color: hasTodayGain ? (todayGain >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500',
    },
    {
      label: 'Total Return',
      value: hasCostBasis ? ((combinedTotalReturn >= 0 ? '+' : '') + fmt(combinedTotalReturn)) : '—',
      sub: hasCostBasis
        ? 'vs. your cost basis'
        : hasHoldings ? 'Enter cost basis per holding to see return' : 'Add holdings to track',
      color: hasCostBasis ? (combinedTotalReturn >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500',
    },
    {
      label: 'Accounts',
      value: totalAccounts > 0 ? totalAccounts : '0',
      sub: totalAccounts === 0 ? 'Add your first account below' : `${totalAccounts} account${totalAccounts !== 1 ? 's' : ''} linked`,
      color: totalAccounts > 0 ? 'text-blue-400' : 'text-slate-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => (
          <div key={k.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
            <div className="text-xs text-slate-500 mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Investment Projection */}
      <InvestmentProjection totalValue={totalValue} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Allocation donut */}
        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-sm mb-4">Allocation Across All Accounts</h2>
          {pieData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="text-3xl mb-3">🥧</div>
              <p className="text-slate-500 text-xs">Add holdings to see your allocation</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                    formatter={(v, n) => [fmt(v), n]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {allocation.filter(a => a.value > 0).map(a => (
                  <div key={a.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: a.color }} />
                      <span className="text-slate-400">{a.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-300 font-medium tabular-nums">{fmt(a.value)}</span>
                      <span className="text-slate-500 tabular-nums w-10 text-right">{(totalValue > 0 ? (a.value / totalValue) * 100 : 0).toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Top holdings + insights */}
        <div className="lg:col-span-3 space-y-4">
          {/* Insights — only render when user has real holdings */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Portfolio Insights</div>
            {!hasHoldings ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="text-2xl mb-2">💡</div>
                <p className="text-slate-500 text-xs">Add holdings to see personalized insights.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(() => {
                  const lcusPct = totalValue > 0 ? (allocation.find(a => a.name === 'Large Cap US')?.value || 0) / totalValue * 100 : 0;
                  const cashPct = totalValue > 0 ? (allocation.find(a => a.name === 'Cash')?.value || 0) / totalValue * 100 : 0;
                  const bondPct = totalValue > 0 ? (allocation.find(a => a.name === 'Bonds')?.value || 0) / totalValue * 100 : 0;
                  const insights = [];
                  if (lcusPct > 60)   insights.push({ icon: '⚠️', color: 'text-amber-400 bg-amber-900/30 border-amber-700/30', msg: `Large Cap US is ${lcusPct.toFixed(0)}% of your portfolio — consider adding more international diversification.` });
                  if (cashPct > 10)   insights.push({ icon: '💡', color: 'text-blue-400 bg-blue-900/30 border-blue-700/30', msg: `Cash & equivalents are ${cashPct.toFixed(0)}% of your portfolio — deploying idle cash could boost long-term returns.` });
                  if (bondPct > 0)    insights.push({ icon: '✅', color: 'text-emerald-400 bg-emerald-900/30 border-emerald-700/30', msg: `Bond allocation at ${bondPct.toFixed(0)}% provides stability. Adjust based on your risk tolerance and timeline.` });
                  if (hasCostBasis && combinedTotalReturn > 0) insights.push({ icon: '📈', color: 'text-emerald-400 bg-emerald-900/30 border-emerald-700/30', msg: `You're up ${fmt(combinedTotalReturn)} over your cost basis — great progress!` });
                  if (insights.length === 0) insights.push({ icon: '✅', color: 'text-emerald-400 bg-emerald-900/30 border-emerald-700/30', msg: 'Your portfolio looks well-diversified. Add cost basis to see gain/loss insights.' });
                  return insights.map((ins, i) => (
                    <div key={i} className={`flex items-start gap-2.5 text-xs p-2.5 rounded-lg border ${ins.color}`}>
                      <span>{ins.icon}</span>
                      <span className="leading-relaxed">{ins.msg}</span>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>

          {/* Top holdings — only render real holdings with value > 0 */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Top Holdings (All Accounts)</div>
            {topHoldings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="text-2xl mb-2">📊</div>
                <p className="text-slate-500 text-xs">
                  {hasHoldings ? 'Prices are loading — check back shortly.' : 'Add holdings from the Overview tab to see them here.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {topHoldings.map(h => (
                  <div key={`${h.ticker}-${h.accountId}`} className="flex items-center gap-3">
                    <div className="w-10 text-xs font-bold text-slate-300 tabular-nums">{h.ticker}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs text-slate-400 truncate">{h.name}</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-1 overflow-hidden">
                        <div
                          className="h-1 rounded-full"
                          style={{ width: `${topHoldings[0].value > 0 ? (h.value / topHoldings[0].value) * 100 : 0}%`, background: ASSET_COLORS[h.assetClass] || '#64748b' }}
                        />
                      </div>
                    </div>
                    <div className="text-xs font-semibold text-slate-200 tabular-nums w-16 text-right">{fmt(h.value)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Linked accounts grid */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-semibold text-slate-200 text-base">Connected Accounts</h2>
          <div className="flex items-center gap-3">
            {!isFreePlan && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <RefreshCw size={10} /> Synced today
              </div>
            )}
            <button
              onClick={handleLinkClick}
              className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
            >
              <Plus size={12} /> Link Account
            </button>
          </div>
        </div>
        {/* Pro upsell — subtle, no button */}
        <p className="text-xs text-slate-600 mb-4">
          Want automatic sync?{' '}
          <span className="text-slate-500">Plaid integration coming soon with Pro.</span>
        </p>

        {/* User-linked investment accounts (real data from Supabase) */}
        {userLinkedAccounts.length > 0 && (
          <div className="space-y-3 mb-4">
            {userLinkedAccounts.map(account => {
              const holdings = account.holdings || [];
              const liveVal = holdings.reduce((s, h) => {
                const price = livePrices[(h.ticker || '').toUpperCase()];
                return s + (h.shares || 0) * (price ?? h.currentPrice ?? 0);
              }, 0);
              const displayVal = liveVal > 0 ? liveVal : (account.balance || 0);
              return (
                <div key={account.id} className="flex items-center gap-3 p-3 bg-slate-700/40 rounded-xl border border-slate-600/40 group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-slate-200 truncate">{account.name}</span>
                      <ConnectionBadge account={account} />
                      <span className="text-xs text-slate-600 bg-slate-700/60 px-1.5 py-0.5 rounded">{ACCOUNT_TYPE_LABELS[account.type] || account.type}</span>
                      {account.connectionType === 'manual' && (
                        <span className="text-xs text-slate-600 bg-slate-700/40 px-1.5 py-0.5 rounded">Manual</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{account.institution || 'Manual entry'}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-sm font-bold text-slate-100 tabular-nums">{fmt(displayVal)}</div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setEditingAccount(account)} title="Edit account"
                        className="p-1.5 rounded-lg hover:bg-slate-600 text-slate-400 hover:text-slate-200 transition-colors">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => setDeletingAccount(account)} title="Delete account"
                        className="p-1.5 rounded-lg hover:bg-rose-900/40 text-slate-400 hover:text-rose-400 transition-colors">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {userLinkedAccounts.length === 0 && (
          <div className="text-center py-6">
            <p className="text-slate-500 text-sm mb-3">No investment accounts yet</p>
            <button
              onClick={handleLinkClick}
              className="flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors font-medium mx-auto"
            >
              <Link2 size={14} /> Add Your First Account
            </button>
          </div>
        )}

        {/* Link more CTA */}
        {userLinkedAccounts.length > 0 && (
          <div className="pt-3 border-t border-slate-700/50 flex items-center justify-between">
            <span className="text-xs text-slate-500">Add more accounts to see your full portfolio picture</span>
            <button
              onClick={handleLinkClick}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-700/40 hover:bg-slate-700 border border-slate-600/40 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Link2 size={10} />
              <span>Add another</span>
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showLinkModal && (
        <AccountLinkModal onClose={() => setShowLinkModal(false)} context="investing" />
      )}
      {showManualModal && (
        <ManualInvestmentModal
          onClose={() => setShowManualModal(false)}
          onSave={handleSaveManualAccount}
        />
      )}
      {editingAccount && (
        <EditAccountModal
          account={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSave={handleEditAccount}
        />
      )}
      {deletingAccount && (
        <DeleteConfirmDialog
          title={`Delete ${deletingAccount.name}?`}
          body={`This will permanently remove "${deletingAccount.name}" and all its holdings. This cannot be undone.`}
          confirmLabel="Delete Account"
          onConfirm={() => handleDeleteAccount(deletingAccount.id)}
          onCancel={() => setDeletingAccount(null)}
        />
      )}
    </div>
  );
}

// ─── Holdings view ────────────────────────────────────────────────────────────
function HoldingsView({ allHoldings, totalValue, priceDate, livePrices = {} }) {
  const { linkedAccountsData, userProfile, updateSection } = useUserData();
  const [filterAccount,   setFilterAccount]   = useState('all');
  const [sortBy,          setSortBy]          = useState('value');
  const [showAddHolding,  setShowAddHolding]  = useState(false);
  const [editingHolding,  setEditingHolding]  = useState(null);
  const [deletingHolding, setDeletingHolding] = useState(null);

  const INVESTMENT_TYPES = ['brokerage', '401k', 'roth_ira', 'ira', 'crypto', 'real_estate', 'other'];
  const manualAccounts = (linkedAccountsData || []).filter(a => a.isInvestmentAccount || INVESTMENT_TYPES.includes(a.type));

  // Enrich every holding with the latest live price at render time.
  // This is the authoritative price source — livePrices wins over h.currentPrice.
  const enrich = (h) => {
    const ticker    = (h.ticker || '').toUpperCase();
    const livePrice = livePrices[ticker];                  // undefined if not yet fetched
    const price     = livePrice != null ? livePrice : (h.currentPrice || 0);
    const shares    = h.shares || 0;
    const value     = shares * price;
    const cb        = h.costBasisPerShare || 0;
    const cbTotal   = h.costBasisTotal != null ? h.costBasisTotal : (cb > 0 ? shares * cb : null);
    return { ...h, currentPrice: price, value, costBasisTotal: cbTotal };
  };

  const uniqueAccounts = [
    { id: 'all', name: 'All Accounts' },
    ...Array.from(new Map(allHoldings.map(h => [h.accountId, { id: h.accountId, name: h.accountName }])).values()),
  ];

  const filtered = allHoldings
    .filter(h => filterAccount === 'all' || h.accountId === filterAccount)
    .map(enrich)   // ← apply live prices here, every render
    .sort((a, b) =>
      sortBy === 'value'  ? b.value - a.value :
      sortBy === 'ticker' ? (a.ticker || '').localeCompare(b.ticker || '') :
      sortBy === 'gl'     ? (b.costBasisTotal !== null ? b.value - b.costBasisTotal : -Infinity) - (a.costBasisTotal !== null ? a.value - a.costBasisTotal : -Infinity) :
      b.dayChangePct - a.dayChangePct
    );

  // Totals computed from enriched rows
  const totalCurrentValue = filtered.reduce((s, h) => s + h.value, 0);
  const totalGL = filtered.reduce((s, h) => s + (h.costBasisTotal !== null ? h.value - h.costBasisTotal : 0), 0);
  const totalCB = filtered.reduce((s, h) => s + (h.costBasisTotal ?? 0), 0);

  const handleAddHolding = (accountId, holding) => {
    const updated = (linkedAccountsData || []).map(acc => {
      if (acc.id !== accountId) return acc;
      return { ...acc, holdings: [...(acc.holdings || []), holding] };
    });
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
  };

  const handleEditHolding = (updatedHolding) => {
    const updated = (linkedAccountsData || []).map(acc => {
      if (acc.id !== updatedHolding.accountId) return acc;
      const newHoldings = (acc.holdings || []).map(h =>
        (h.id || h.ticker) === (updatedHolding.holdingId || updatedHolding.ticker) ? {
          ...h,
          lots: updatedHolding.lots,
          shares: updatedHolding.shares,
          costBasisPerShare: updatedHolding.costBasisPerShare,
          costBasisTotal: updatedHolding.costBasisTotal,
        } : h
      );
      return { ...acc, holdings: newHoldings };
    });
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
    setEditingHolding(null);
  };

  const handleDeleteHolding = (h) => {
    const updated = (linkedAccountsData || []).map(acc => {
      if (acc.id !== h.accountId) return acc;
      const newHoldings = (acc.holdings || []).filter(hh =>
        (hh.id || hh.ticker) !== (h.holdingId || h.ticker)
      );
      return { ...acc, holdings: newHoldings };
    });
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
    setDeletingHolding(null);
  };

  return (
    <div className="space-y-4">
      {/* Account filter pills + sort controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {uniqueAccounts.map(a => (
          <button key={a.id} onClick={() => setFilterAccount(a.id)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
              filterAccount === a.id ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-slate-200'
            }`}
          >{a.name}</button>
        ))}
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-slate-500">
            Sort:
            {[['value', 'Value'], ['ticker', 'Ticker'], ['gl', 'G/L']].map(([k, l]) => (
              <button key={k} onClick={() => setSortBy(k)} className={`px-2 py-1 rounded transition-colors ${sortBy === k ? 'text-blue-400 bg-blue-900/30' : 'hover:text-slate-300'}`}>{l}</button>
            ))}
          </div>
          {manualAccounts.length > 0 && (
            <button onClick={() => setShowAddHolding(true)}
              className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
            >
              <Plus size={11} /> Add Holding
            </button>
          )}
        </div>
      </div>

      {allHoldings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-slate-800 border border-slate-700 rounded-xl">
          <div className="text-3xl mb-3">📊</div>
          <p className="text-slate-400 text-sm mb-1">No holdings yet</p>
          <p className="text-slate-500 text-xs">Add an account with holdings from the Overview tab.</p>
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden overflow-x-auto">
          {/* Table header — 13 cols: name | ticker | account | shares | price | value | cost basis | G/L$ | G/L% | actions */}
          <div className="grid gap-2 px-4 py-2.5 text-xs text-slate-500 font-medium uppercase tracking-wider border-b border-slate-700 min-w-[820px]"
            style={{ gridTemplateColumns: '2fr 0.7fr 1.5fr 0.7fr 0.9fr 1.1fr 1.1fr 1.1fr 0.7fr 56px' }}>
            <div>Asset</div>
            <div>Ticker</div>
            <div>Account</div>
            <div className="text-right">Shares</div>
            <div className="text-right">Price</div>
            <div className="text-right">Value</div>
            <div className="text-right">Avg. Cost</div>
            <div className="text-right">Gain / Loss</div>
            <div className="text-right">G/L %</div>
            <div />
          </div>

          {filtered.map((h, i) => {
            // h is already enriched with live price by enrich() above
            const hasLivePrice = h.currentPrice > 0;
            const gl    = h.costBasisTotal !== null && hasLivePrice ? h.value - h.costBasisTotal : null;
            const glPct = gl !== null && h.costBasisTotal > 0 ? (gl / h.costBasisTotal) * 100 : null;
            const priceStr = hasLivePrice
              ? `$${Number(h.currentPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '—';
            return (
              <div key={`${h.ticker}-${h.accountId}-${i}`}
                className="grid gap-2 px-4 py-3 text-xs border-b border-slate-700/40 hover:bg-slate-700/20 transition-colors last:border-0 group min-w-[820px] items-center"
                style={{ gridTemplateColumns: '2fr 0.7fr 1.5fr 0.7fr 0.9fr 1.1fr 1.1fr 1.1fr 0.7fr 56px' }}
              >
                <div className="text-slate-400 truncate">{h.name}</div>
                <div className="font-bold text-slate-200 font-mono">{h.ticker}</div>
                <div className="text-slate-500 truncate">{h.accountName}</div>
                <div className="text-right text-slate-400 tabular-nums">{h.shares ? Number(h.shares).toLocaleString() : '—'}</div>
                <div className="text-right text-slate-500 tabular-nums">{priceStr}</div>
                <div className="text-right font-semibold text-slate-200 tabular-nums">
                  {hasLivePrice ? fmt(h.value) : <span className="text-slate-600 italic text-xs">No price</span>}
                </div>
                <div className="text-right text-slate-500 tabular-nums">
                  {h.costBasisPerShare != null && h.costBasisPerShare > 0
                    ? `$${Number(h.costBasisPerShare).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </div>
                <div className={`text-right font-semibold tabular-nums ${gl === null ? 'text-slate-600' : gl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {gl === null ? '—' : (gl >= 0 ? '+' : '') + fmt(gl)}
                </div>
                <div className={`text-right tabular-nums font-medium ${glPct === null ? 'text-slate-600' : glPct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {glPct === null ? '—' : (glPct >= 0 ? '+' : '') + glPct.toFixed(1) + '%'}
                </div>
                {/* Edit / delete — only for manual holdings */}
                <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {h.isManual && (<>
                    <button onClick={() => setEditingHolding(h)} title="Edit holding"
                      className="p-1 rounded hover:bg-slate-600 text-slate-500 hover:text-slate-200 transition-colors">
                      <Pencil size={11} />
                    </button>
                    <button onClick={() => setDeletingHolding(h)} title="Remove holding"
                      className="p-1 rounded hover:bg-rose-900/40 text-slate-500 hover:text-rose-400 transition-colors">
                      <Trash2 size={11} />
                    </button>
                  </>)}
                </div>
              </div>
            );
          })}

          {/* Total row */}
          <div className="grid gap-2 px-4 py-3 bg-slate-700/30 border-t border-slate-600 text-xs font-bold min-w-[820px]"
            style={{ gridTemplateColumns: '2fr 0.7fr 1.5fr 0.7fr 0.9fr 1.1fr 1.1fr 1.1fr 0.7fr 56px' }}>
            <div className="text-slate-400 col-span-1">Total ({filtered.length})</div>
            <div /><div /><div /><div />
            <div className="text-right text-slate-200 tabular-nums">{fmt(totalCurrentValue)}</div>
            <div className="text-right text-slate-500 tabular-nums">{totalCB > 0 ? fmt(totalCB) : '—'}</div>
            <div className={`text-right tabular-nums ${totalGL >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {totalCB > 0 ? (totalGL >= 0 ? '+' : '') + fmt(totalGL) : '—'}
            </div>
            <div className={`text-right tabular-nums ${totalGL >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {totalCB > 0 ? (totalGL >= 0 ? '+' : '') + ((totalGL / totalCB) * 100).toFixed(1) + '%' : '—'}
            </div>
            <div />
          </div>
        </div>
      )}

      {/* Prices as of note */}
      {allHoldings.length > 0 && (
        <p className="text-xs text-slate-600 text-right">
          {priceDate ? `Prices as of ${priceDate}` : process.env.REACT_APP_ALPHA_VANTAGE_KEY ? 'Loading prices…' : 'Add REACT_APP_ALPHA_VANTAGE_KEY to .env to enable live prices'}
        </p>
      )}

      {showAddHolding && (
        <AddHoldingModal
          accounts={manualAccounts}
          onClose={() => setShowAddHolding(false)}
          onSave={handleAddHolding}
        />
      )}
      {editingHolding && (
        <EditHoldingModal
          holding={editingHolding}
          accountName={editingHolding.accountName}
          onClose={() => setEditingHolding(null)}
          onSave={handleEditHolding}
        />
      )}
      {deletingHolding && (
        <DeleteConfirmDialog
          title={`Remove ${deletingHolding.ticker}?`}
          body={`Remove ${deletingHolding.ticker} from ${deletingHolding.accountName}? This cannot be undone.`}
          confirmLabel="Remove"
          onConfirm={() => handleDeleteHolding(deletingHolding)}
          onCancel={() => setDeletingHolding(null)}
        />
      )}
    </div>
  );
}

// ─── Allocation view ──────────────────────────────────────────────────────────
function AllocationView({ allocation, totalValue, investingData }) {
  const { sectorExposure, linkedAccounts, riskProfile: savedRP } = investingData;

  // Build per-account breakdown by asset class
  const accountBreakdowns = linkedAccounts.map(acc => {
    const byClass = {};
    acc.holdings.forEach(h => { byClass[h.assetClass] = (byClass[h.assetClass] || 0) + h.value; });
    return { ...acc, byClass, total: acc.balance };
  });

  const SECTOR_COLORS = ['#3b82f6','#8b5cf6','#ec4899','#f59e0b','#06b6d4','#10b981','#f97316','#64748b','#94a3b8'];

  // ── Market cap breakdown ───────────────────────────────────────────────────
  const MARKET_CAP_MAP = {
    'Large Cap US':  'Large Cap',
    'Mid/Small Cap': 'Mid & Small Cap',
  };
  const mcMap = {};
  allocation.forEach(a => {
    const bucket = MARKET_CAP_MAP[a.name] || 'Other / Fixed Income';
    mcMap[bucket] = (mcMap[bucket] || 0) + a.value;
  });
  const mcData = Object.entries(mcMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  const mcColors = { 'Large Cap': '#3b82f6', 'Mid & Small Cap': '#06b6d4', 'Other / Fixed Income': '#10b981' };

  // ── Geography breakdown ───────────────────────────────────────────────────
  const GEO_MAP = {
    'Large Cap US':  'US Equity',
    'Mid/Small Cap': 'US Equity',
    'REITs':         'US Equity',
    'Sector':        'US Equity',
    'Cash':          'US Equity',
    'International': 'International',
    'Bonds':         'Fixed Income',
    'Alternatives':  'Alternatives',
  };
  const geoMap = {};
  allocation.forEach(a => {
    const bucket = GEO_MAP[a.name] || 'Other';
    geoMap[bucket] = (geoMap[bucket] || 0) + a.value;
  });
  const geoOrder = ['US Equity', 'International', 'Fixed Income', 'Alternatives', 'Other'];
  const geoData = geoOrder
    .filter(g => geoMap[g])
    .map(name => ({ name, value: geoMap[name] }));
  const geoColors = { 'US Equity': '#3b82f6', 'International': '#8b5cf6', 'Fixed Income': '#10b981', 'Alternatives': '#f97316', 'Other': '#64748b' };

  // ── Overweight/underweight warnings (only when risk profile saved) ────────
  let overweightWarnings = [];
  if (savedRP && PROFILES[savedRP.key]) {
    const targetAlloc = PROFILES[savedRP.key].allocation;
    const currentPcts = {};
    allocation.forEach(a => { currentPcts[a.name] = (a.value / totalValue) * 100; });
    overweightWarnings = Object.keys(ASSET_COLORS)
      .map(cls => ({ cls, target: targetAlloc[cls] || 0, actual: currentPcts[cls] || 0 }))
      .map(w => ({ ...w, diff: w.actual - w.target }))
      .filter(w => Math.abs(w.diff) >= 5)
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  }

  return (
    <div className="space-y-6">
      {/* Asset class breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-sm mb-4">Asset Class Breakdown</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={allocation} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={2} dataKey="value">
                {allocation.map((a, i) => <Cell key={i} fill={a.color} />)}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                formatter={(v, n) => [`${fmt(v)} (${(totalValue > 0 ? (v / totalValue) * 100 : 0).toFixed(1)}%)`, n]}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="space-y-2 mt-2">
            {allocation.map(a => {
              const pct = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
              return (
                <div key={a.name} className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: a.color }} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-300 font-medium">{a.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 tabular-nums">{fmt(a.value)}</span>
                        <span className="text-xs font-bold text-slate-300 tabular-nums w-10 text-right">{pct.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
                      <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: a.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sector exposure */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-sm mb-1">Sector Exposure</h2>
          <p className="text-xs text-slate-500 mb-4">Blended across all equity holdings (index fund weights included)</p>
          {(sectorExposure || []).length === 0 ? (
            <div className="text-xs text-slate-500 py-4 text-center">No sector data — add holdings with ticker symbols to see breakdown.</div>
          ) : (
            <>
              <div className="space-y-3">
                {(sectorExposure || []).map((s, i) => (
                  <div key={s.sector} className="flex items-center gap-3">
                    <div className="w-28 text-xs text-slate-400 text-right flex-shrink-0">{s.sector}</div>
                    <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${(s.pct / sectorExposure[0].pct) * 100}%`, background: SECTOR_COLORS[i] }}
                      />
                    </div>
                    <div className="text-xs font-bold tabular-nums text-slate-300 w-10 flex-shrink-0">{s.pct}%</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-amber-950/30 border border-amber-700/30 rounded-lg text-xs text-amber-300/80 leading-relaxed">
                <Info size={11} className="inline mr-1.5 text-amber-400" />
                Technology at {sectorExposure[0].pct}% is above the S&P 500 index weight (~29%). This is partly driven by your individual NVDA/AAPL/MSFT positions.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Market cap + geography row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Market Cap */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-sm mb-4">Market Cap Breakdown</h2>
          <div className="space-y-3">
            {mcData.map(b => {
              const pct = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
              const color = mcColors[b.name] || '#64748b';
              return (
                <div key={b.name} className="flex items-center gap-3">
                  <div className="w-28 text-xs text-slate-400 text-right flex-shrink-0">{b.name}</div>
                  <div className="flex-1 bg-slate-700 rounded-full h-2.5 overflow-hidden">
                    <div className="h-2.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                  </div>
                  <div className="text-xs font-bold tabular-nums text-slate-300 w-12 text-right">{pct.toFixed(1)}%</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Geography */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-sm mb-4">Geographic Exposure</h2>
          <div className="space-y-3">
            {geoData.map(b => {
              const pct = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
              const color = geoColors[b.name] || '#64748b';
              return (
                <div key={b.name} className="flex items-center gap-3">
                  <div className="w-28 text-xs text-slate-400 text-right flex-shrink-0">{b.name}</div>
                  <div className="flex-1 bg-slate-700 rounded-full h-2.5 overflow-hidden">
                    <div className="h-2.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                  </div>
                  <div className="text-xs font-bold tabular-nums text-slate-300 w-12 text-right">{pct.toFixed(1)}%</div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-slate-600 leading-relaxed">
            <Info size={10} className="inline mr-1 text-slate-500" />
            Geographic classification is based on asset class labels in your holdings.
          </div>
        </div>
      </div>

      {/* Overweight/underweight warnings */}
      {overweightWarnings.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-sm mb-1">Allocation Drift vs Your Risk Profile</h2>
          <p className="text-xs text-slate-500 mb-4">
            Compared to target allocation for <strong className="text-slate-300">{PROFILES[savedRP.key]?.name}</strong> profile (≥5% drift shown)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {overweightWarnings.map(w => (
              <div key={w.cls} className={`flex items-center gap-3 p-3 rounded-xl border text-xs ${
                w.diff > 0
                  ? 'bg-amber-950/30 border-amber-700/30'
                  : 'bg-blue-950/30 border-blue-700/30'
              }`}>
                <div className={`font-bold text-base flex-shrink-0 ${w.diff > 0 ? 'text-amber-400' : 'text-blue-400'}`}>
                  {w.diff > 0 ? '⚠' : '↓'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold mb-0.5 ${w.diff > 0 ? 'text-amber-300' : 'text-blue-300'}`}>
                    {w.diff > 0 ? 'Overweight' : 'Underweight'} in {w.cls}
                  </div>
                  <div className="text-slate-500 tabular-nums">
                    {w.actual.toFixed(1)}% actual vs {w.target}% target
                  </div>
                </div>
                <div className={`font-bold tabular-nums flex-shrink-0 ${w.diff > 0 ? 'text-amber-400' : 'text-blue-400'}`}>
                  {w.diff > 0 ? '+' : ''}{w.diff.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-start gap-2 p-3 bg-slate-700/30 rounded-xl text-xs text-slate-400 leading-relaxed">
            <Info size={11} className="text-blue-400 flex-shrink-0 mt-0.5" />
            Drift is calculated relative to your saved risk profile target. Go to the Risk Profile tab to rebalance.
          </div>
        </div>
      )}

      {/* Per-account stacked allocation */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h2 className="font-semibold text-slate-200 text-sm mb-4">How Each Account Contributes</h2>
        <div className="space-y-4">
          {accountBreakdowns.map(acc => (
            <div key={acc.id} className="flex items-center gap-4">
              <div className="w-36 flex-shrink-0">
                <div className="text-xs font-semibold text-slate-300 truncate">{acc.name}</div>
                <div className="text-xs text-slate-500">{fmt(acc.balance)} total</div>
              </div>
              <div className="flex-1 h-5 flex rounded-md overflow-hidden gap-px">
                {Object.entries(acc.byClass).sort((a, b) => b[1] - a[1]).map(([cls, val]) => {
                  const w = acc.balance > 0 ? (val / acc.balance) * 100 : 0;
                  if (w < 1) return null;
                  return (
                    <div
                      key={cls}
                      title={`${cls}: ${fmt(val)} (${w.toFixed(1)}%)`}
                      style={{ width: `${w}%`, background: ASSET_COLORS[cls] || '#64748b' }}
                    />
                  );
                })}
              </div>
              <div className="w-24 flex-shrink-0">
                <div className="flex flex-wrap gap-1">
                  {Object.entries(acc.byClass).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([cls]) => (
                    <span key={cls} className="text-xs px-1 py-0.5 rounded" style={{ background: (ASSET_COLORS[cls] || '#64748b') + '33', color: ASSET_COLORS[cls] || '#94a3b8' }}>
                      {cls.split(' ')[0]}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {/* Legend */}
          <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-700/50">
            {Object.entries(ASSET_COLORS).map(([name, color]) => (
              <div key={name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
                <span className="text-xs text-slate-500">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Simulator view ───────────────────────────────────────────────────────────
function SimulatorView({ totalValue, allocation }) {
  const { userProfile } = useUserData();
  const [startValue, setStartValue]   = useState(Math.round(totalValue / 1000) * 1000);
  const [monthlyAdd, setMonthlyAdd]   = useState(2000);
  const [years, setYears]             = useState(20);
  const [expReturn, setExpReturn]     = useState(8);
  const [vol, setVol]                 = useState(15);
  const [result, setResult]           = useState(null);
  const [resultReal, setResultReal]   = useState(null);
  const [running, setRunning]         = useState(false);
  const [scenarios, setScenarios]     = useState(null);
  const [inflationRate, setInflationRate] = useState(3.0);
  // Dismissible explainer — persisted to localStorage so it only shows once
  const [showExplainer, setShowExplainer] = useState(
    () => { try { return localStorage.getItem('finovo_mc_explainer') !== '1'; } catch { return true; } }
  );

  const runSim = useCallback(() => {
    const realReturn = parseFloat((expReturn - inflationRate).toFixed(1));
    setRunning(true);
    setTimeout(() => {
      const r     = runInvestmentMC({ startValue, monthlyContrib: monthlyAdd, annualReturn: expReturn,  annualVol: vol, years, numSims: 1000 });
      const rReal = runInvestmentMC({ startValue, monthlyContrib: monthlyAdd, annualReturn: realReturn, annualVol: vol, years, numSims: 1000 });
      setResult(r);
      setResultReal(rReal);
      setRunning(false);
    }, 50);
  }, [startValue, monthlyAdd, expReturn, inflationRate, vol, years]);

  const runComparison = useCallback(() => {
    const realReturn = parseFloat((expReturn - inflationRate).toFixed(1));
    setRunning(true);
    setTimeout(() => {
      const base = { startValue, monthlyContrib: monthlyAdd, years, numSims: 1000 };
      const s = {
        conservative: runInvestmentMC({ ...base, annualReturn: 6,          annualVol: 10 }),
        moderate:     runInvestmentMC({ ...base, annualReturn: expReturn,   annualVol: vol }),
        aggressive:   runInvestmentMC({ ...base, annualReturn: 10,          annualVol: 22 }),
      };
      setScenarios(s);
      setResult(s.moderate);
      setResultReal(runInvestmentMC({ ...base, annualReturn: realReturn, annualVol: vol }));
      setRunning(false);
    }, 80);
  }, [startValue, monthlyAdd, expReturn, inflationRate, vol, years]);

  const loadFromPortfolio = useCallback(() => {
    if (!allocation || !allocation.length) return;
    const total = allocation.reduce((s, a) => s + a.value, 0);
    if (!total) return;
    let wReturn = 0, wVol = 0;
    allocation.forEach(a => {
      const w = a.value / total;
      const stats = ASSET_CLASS_STATS[a.name];
      if (!stats) return;
      wReturn += w * stats.return;
      wVol    += w * stats.vol;
    });
    setStartValue(Math.round(total / 1000) * 1000);
    setExpReturn(parseFloat(wReturn.toFixed(1)));
    setVol(Math.round(wVol * 0.75)); // 0.75 = diversification discount
  }, [allocation]);

  // Auto-run on mount
  useEffect(() => { runSim(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const final     = result?.percentiles[result.percentiles.length - 1];
  const finalReal = resultReal?.percentiles[resultReal.percentiles.length - 1];

  return (
    <div className="space-y-6">

      {/* ── Dismissible explainer banner ── */}
      {showExplainer && (
        <div className="bg-blue-950/40 border border-blue-700/40 rounded-xl p-4 flex gap-3 items-start">
          <span className="text-2xl flex-shrink-0 mt-0.5">🎲</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-300 leading-relaxed">
              This tool runs <span className="text-blue-300 font-semibold">1,000 different scenarios</span> for your portfolio based on historical market data.
              Think of it like asking: <em className="text-slate-200">if I keep investing this way, what could my portfolio look like in 10, 20, or 30 years?</em>{' '}
              Some scenarios show great years, some show tough ones — just like real life.
              The chart below shows you the <span className="text-blue-300 font-semibold">range of possible outcomes</span> so you can plan with confidence.
            </p>
          </div>
          <button
            onClick={() => { setShowExplainer(false); try { localStorage.setItem('finovo_mc_explainer', '1'); } catch {} }}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-300 hover:text-blue-200 border border-blue-700/50 hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            Got it ✓
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-5">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-slate-200 text-sm">Simulator Inputs</h2>
            <span className="text-xs text-slate-500 bg-slate-700/60 px-2 py-0.5 rounded-full">1,000 sims</span>
          </div>
          {allocation && allocation.length > 0 && (
            <button
              onClick={loadFromPortfolio}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-blue-300 border border-blue-700/40 bg-blue-900/20 hover:bg-blue-900/40 py-2 rounded-lg transition-colors"
            >
              <RefreshCw size={11} /> Load Portfolio Data
            </button>
          )}

          <Slider label="Starting Value"       value={startValue}  min={10000}  max={1000000} step={5000}  onChange={setStartValue}  fmtFn={fmt} />
          <Slider label="Monthly Contribution" value={monthlyAdd}  min={0}      max={10000}   step={100}   onChange={setMonthlyAdd}  fmtFn={fmt} />
          <Slider label="Time Horizon"         value={years}       min={1}      max={40}      step={1}     onChange={setYears}       fmtFn={v => `${v} yrs`} />
          {/* Custom slider with InfoTooltip on label — Expected Return */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-400 font-medium flex items-center">
                Expected Annual Return<InfoTooltip text={TOOLTIPS.annualizedReturn} />
              </span>
              <span className="text-sm font-bold text-slate-100">{expReturn}%</span>
            </div>
            <input type="range" min={2} max={15} step={0.5} value={expReturn} onChange={e => setExpReturn(Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none bg-slate-700 accent-blue-500 cursor-pointer" />
            <div className="text-xs text-slate-600 mt-1">Historical S&P 500 avg: ~10% nominal, ~7% real</div>
          </div>
          {/* Custom slider with InfoTooltip on label — Volatility */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-400 font-medium flex items-center">
                Annual Volatility<InfoTooltip text={TOOLTIPS.volatility} />
              </span>
              <span className="text-sm font-bold text-slate-100">{vol}%</span>
            </div>
            <input type="range" min={5} max={35} step={1} value={vol} onChange={e => setVol(Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none bg-slate-700 accent-blue-500 cursor-pointer" />
            <div className="text-xs text-slate-600 mt-1">S&P 500 historical vol: ~15–17%</div>
          </div>

          {/* Inflation rate — always visible */}
          <div className="pt-1 border-t border-slate-700/50">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-400 font-medium">Inflation Rate</span>
              <span className="text-sm font-bold text-slate-100">{inflationRate}%</span>
            </div>
            <input type="range" min={1} max={6} step={0.5} value={inflationRate}
              onChange={e => setInflationRate(Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none bg-slate-700 accent-emerald-500 cursor-pointer" />
            <div className="text-xs text-slate-600 mt-1">Historical US average: ~3%/year</div>
            <div className="mt-2 text-xs text-emerald-400/70 bg-emerald-900/10 border border-emerald-700/20 rounded-lg px-2.5 py-1.5">
              Real return: <span className="font-semibold">{parseFloat((expReturn - inflationRate).toFixed(1))}%</span>
              <span className="text-slate-600 ml-1">({expReturn}% − {inflationRate}%)</span>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={runSim}
              disabled={running}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold py-2 rounded-lg transition-colors"
            >
              {running ? 'Running…' : 'Run Simulation'}
            </button>
            <button
              onClick={runComparison}
              disabled={running}
              className="flex-1 bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-white text-xs font-semibold py-2 rounded-lg transition-colors flex items-center justify-center gap-1"
            >
              <Zap size={11} /> Compare Scenarios
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-semibold text-slate-200 text-sm flex items-center">
              Monte Carlo<InfoTooltip text={TOOLTIPS.monteCarlo} side="bottom" /> — Portfolio Growth
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Shaded = P25–P75 · Blue = Nominal · Green = Real</span>
              <button
                onClick={() => window.print()}
                title="Print"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-700 transition-colors"
              ><Printer size={13} /></button>
              <button
                onClick={() => generateSimulatorReport({
                  startValue, monthlyAdd, years, expReturn, vol, result,
                  userName: userProfile?.first_name || userProfile?.display_name || '',
                })}
                title="Download Report"
                className="p-1.5 rounded-lg text-slate-500 hover:text-blue-400 hover:bg-slate-700 transition-colors"
              ><Download size={13} /></button>
            </div>
          </div>
          <div className="h-64 flex items-stretch gap-1">
            {result && (
              <div className="flex items-center justify-center flex-shrink-0"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '10px', color: '#475569', whiteSpace: 'nowrap' }}>
                Portfolio Value
              </div>
            )}
            <div className="flex-1">
              {result ? <InvestmentCanvas result={result} resultReal={resultReal} years={years} /> : (
                <div className="h-full flex items-center justify-center text-slate-600 text-sm">Hit "Run Simulation" to start</div>
              )}
            </div>
          </div>

          {/* Chart legend */}
          {result && (
            <div className="flex items-center gap-4 mt-2 justify-end">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <div className="w-6 h-0.5 bg-blue-500 rounded" />
                Nominal (future $)
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <div className="w-6 h-0.5 bg-emerald-400 rounded" />
                Real (today's $ at {inflationRate}% inflation)
              </div>
            </div>
          )}

          {/* Percentile cards — dual column: nominal | real */}
          {result && final && finalReal && (
            <>
              {/* Column headers */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="text-center">
                  <span className="text-xs font-semibold text-blue-400 bg-blue-900/20 px-2.5 py-1 rounded-full">Future Value (Nominal)</span>
                  <p className="text-xs text-slate-600 mt-1">In future dollars</p>
                </div>
                <div className="text-center">
                  <span className="text-xs font-semibold text-emerald-400 bg-emerald-900/20 px-2.5 py-1 rounded-full">Today's Purchasing Power</span>
                  <p className="text-xs text-slate-600 mt-1">In today's dollars (inflation-adjusted)</p>
                </div>
              </div>

              {/* Percentile rows */}
              <div className="mt-2 space-y-1.5">
                {[
                  { label: 'P10 (Bear)', key: 'p10', color: 'text-rose-400',    note: 'worst 10%' },
                  { label: 'P25',        key: 'p25', color: 'text-amber-400',   note: 'below avg' },
                  { label: 'P50 Median', key: 'p50', color: 'text-blue-400',    note: 'median',    tooltip: true },
                  { label: 'P75',        key: 'p75', color: 'text-emerald-400', note: 'above avg' },
                  { label: 'P90 (Bull)', key: 'p90', color: 'text-emerald-300', note: 'top 10%' },
                ].map(p => (
                  <div key={p.label} className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center bg-slate-700/30 rounded-lg px-3 py-2">
                    <div className="text-xs text-slate-500 flex items-center gap-0.5 font-medium">
                      {p.label}{p.tooltip && <InfoTooltip text={TOOLTIPS.percentile} />}
                    </div>
                    <div className={`text-sm font-bold tabular-nums text-right ${p.color}`}>
                      {fmtC(final[p.key])}
                    </div>
                    <div className="text-sm font-bold tabular-nums text-right text-emerald-400/80">
                      {fmtC(finalReal[p.key])}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Plain English Summary ── */}
              <div className="mt-5 p-4 bg-slate-700/30 border border-slate-600/40 rounded-xl space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-base">📋</span>
                  <h3 className="text-sm font-semibold text-slate-200">What this means for you</h3>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Based on <span className="text-slate-300 font-medium">1,000 simulations</span>, if you keep investing this way for{' '}
                  <span className="text-slate-300 font-medium">{years} year{years !== 1 ? 's' : ''}</span>:
                </p>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2.5 p-2.5 bg-emerald-950/30 border border-emerald-700/20 rounded-lg">
                    <span className="text-emerald-400 font-bold text-base leading-none mt-0.5 flex-shrink-0">↑</span>
                    <span className="text-slate-300">In a <span className="text-emerald-400 font-semibold">great scenario (top 10%)</span>, your portfolio will be worth{' '}
                      <span className="text-blue-400 font-bold tabular-nums">{fmtC(final.p90)}</span>{' '}
                      — equivalent to <span className="text-emerald-400 font-bold tabular-nums">{fmtC(finalReal.p90)}</span> in today's purchasing power.
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5 p-2.5 bg-blue-950/30 border border-blue-700/20 rounded-lg">
                    <span className="text-blue-400 font-bold text-base leading-none mt-0.5 flex-shrink-0">→</span>
                    <span className="text-slate-300">In a <span className="text-blue-400 font-semibold">typical scenario (middle)</span>, your portfolio will be worth{' '}
                      <span className="text-blue-400 font-bold tabular-nums">{fmtC(final.p50)}</span>{' '}
                      — equivalent to <span className="text-emerald-400 font-bold tabular-nums">{fmtC(finalReal.p50)}</span> in today's purchasing power.
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5 p-2.5 bg-rose-950/30 border border-rose-700/20 rounded-lg">
                    <span className="text-rose-400 font-bold text-base leading-none mt-0.5 flex-shrink-0">↓</span>
                    <span className="text-slate-300">In a <span className="text-rose-400 font-semibold">tough scenario (bottom 10%)</span>, your portfolio might be{' '}
                      <span className="text-blue-400 font-bold tabular-nums">{fmtC(final.p10)}</span>{' '}
                      — equivalent to <span className="text-emerald-400 font-bold tabular-nums">{fmtC(finalReal.p10)}</span> in today's purchasing power.
                    </span>
                  </div>
                </div>
                {final.p50 > startValue && (
                  <p className="text-xs text-slate-500 pt-1 border-t border-slate-600/40">
                    In the median case, your nominal balance grows by{' '}
                    <span className="text-blue-400 font-semibold">{fmtC(final.p50 - startValue)}</span> ({((final.p50 / startValue - 1) * 100).toFixed(0)}% total) —{' '}
                    worth <span className="text-emerald-400 font-semibold">{fmtC(finalReal.p50 - startValue)}</span> more in today's dollars after {inflationRate}% annual inflation.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Scenario comparison */}
      {scenarios && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-sm mb-4">Scenario Comparison — After {years} Years</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: 'conservative', label: 'Conservative', return: 6,        vol: 10, color: 'border-blue-700/40 bg-blue-950/20',     text: 'text-blue-400',    desc: '60/40 balanced portfolio — bonds + equities' },
              { key: 'moderate',     label: 'Moderate',     return: expReturn, vol,     color: 'border-purple-700/40 bg-purple-950/20', text: 'text-purple-400',  desc: 'Your current settings — diversified equity' },
              { key: 'aggressive',   label: 'Aggressive',   return: 10,        vol: 22, color: 'border-emerald-700/40 bg-emerald-950/20', text: 'text-emerald-400', desc: '100% equities, high growth / high risk' },
            ].map(s => {
              const sc   = scenarios[s.key];
              const fin  = sc.percentiles[sc.percentiles.length - 1];
              const gain = fin.p50 - startValue;
              return (
                <div key={s.key} className={`p-4 rounded-xl border ${s.color}`}>
                  <div className={`text-sm font-semibold ${s.text} mb-1`}>{s.label}</div>
                  <div className="text-xs text-slate-500 mb-3">{s.desc}</div>
                  <div className="text-xs text-slate-500 mb-0.5">{s.return}% return · {s.vol}% vol</div>
                  <div className="text-lg font-bold text-slate-100 tabular-nums">{fmtC(fin.p50)}</div>
                  <div className="text-xs text-emerald-400 mb-3">+{fmtC(gain)} median gain</div>
                  <div className="space-y-1.5 pt-2 border-t border-slate-700/50">
                    {[['P90 (best)', fin.p90, 'text-emerald-400'], ['P50 (median)', fin.p50, 'text-blue-400'], ['P10 (worst)', fin.p10, 'text-rose-400']].map(([l, v, c]) => (
                      <div key={l} className="flex justify-between text-xs">
                        <span className="text-slate-500">{l}</span>
                        <span className={`font-semibold tabular-nums ${c}`}>{fmtC(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-slate-400 bg-slate-700/30 p-3 rounded-lg leading-relaxed">
            <Info size={12} className="text-blue-400 flex-shrink-0 mt-0.5" />
            Monte Carlo uses log-normal return distributions (1,000 simulations). Past performance does not guarantee future results. All comparison figures are nominal (future dollars). Use the main simulator for inflation-adjusted projections.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Risk Assessment Data ─────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: 'time_horizon', category: 'Time Horizon', icon: '⏰',
    question: 'When do you plan to start withdrawing from this portfolio?',
    context: "Longer horizons let your investments recover from downturns — it's the single most important factor in determining your ideal allocation.",
    options: [
      { label: 'Less than 3 years',   sub: 'Short-term — capital preservation is critical',       points: 0 },
      { label: '3–5 years',           sub: 'Medium-term — need some stability',                    points: 1 },
      { label: '6–10 years',          sub: 'Growing horizon — can tolerate moderate risk',          points: 2 },
      { label: '11–20 years',         sub: 'Long horizon — time to recover from downturns',         points: 3 },
      { label: '20+ years',           sub: 'Maximum horizon — can ride out any storm',              points: 4 },
    ],
  },
  {
    id: 'market_drop', category: 'Market Reaction', icon: '📉',
    question: 'Your portfolio drops 20% in 3 months. What do you do?',
    context: "Be honest about how you'd actually behave — not how you think you should. Studies show most investors overestimate their own risk tolerance.",
    options: [
      { label: 'Sell everything immediately',              sub: "Preserve what's left, exit the market",           points: 0 },
      { label: 'Sell some to reduce exposure',             sub: 'Cut risk but stay partially invested',             points: 1 },
      { label: 'Hold steady and wait it out',              sub: 'Markets recover — do nothing',                    points: 2 },
      { label: 'Buy a little more at lower prices',        sub: 'Take advantage of the dip',                       points: 3 },
      { label: 'Buy aggressively — great opportunity',     sub: 'Deploy all spare cash into the downturn',         points: 4 },
    ],
  },
  {
    id: 'max_loss', category: 'Loss Tolerance', icon: '💸',
    question: "What's the largest annual loss you could handle without panic selling?",
    context: 'In dollar terms, based on your current $439k portfolio:',
    options: [
      { label: 'Less than 5%',   sub: 'Under ~$22k — very low tolerance',            points: 0 },
      { label: '5–10%',          sub: '$22k–$44k — small losses are okay',           points: 1 },
      { label: '10–20%',         sub: '$44k–$88k — I can stomach a correction',      points: 2 },
      { label: '20–35%',         sub: "$88k–$154k — I've seen bear markets",         points: 3 },
      { label: 'More than 35%',  sub: 'Over $154k — long-term investor, no panic',   points: 4 },
    ],
  },
  {
    id: 'income', category: 'Income Stability', icon: '💼',
    question: 'How stable and predictable is your current income?',
    context: "Stable income means your portfolio is your growth engine — not your emergency lifeline. The more stable your income, the more risk your investments can carry.",
    options: [
      { label: 'Very unstable (gig / commission / freelance)',  sub: 'Income varies significantly month to month',   points: 0 },
      { label: 'Somewhat unstable',                             sub: 'Occasional gaps or variability',                points: 1 },
      { label: 'Fairly stable (salaried)',                      sub: 'Regular paycheck, low risk of job loss',        points: 2 },
      { label: 'Very stable (government / tenured)',            sub: 'Essentially guaranteed income',                 points: 3 },
      { label: 'Multiple diversified income streams',           sub: 'Employment + side income + passive sources',    points: 4 },
    ],
  },
  {
    id: 'emergency', category: 'Emergency Fund', icon: '🛡️',
    question: 'How many months of living expenses do you have in liquid emergency savings?',
    context: "A solid emergency fund prevents you from being forced to sell investments at the worst possible times — like during a market crash.",
    options: [
      { label: 'Less than 1 month',   sub: 'Vulnerable to unexpected expenses',          points: 0 },
      { label: '1–3 months',          sub: 'Some cushion but limited buffer',             points: 1 },
      { label: '3–6 months',          sub: 'Approaching the recommended minimum',         points: 2 },
      { label: '6–12 months',         sub: 'Well-prepared for most emergencies',          points: 3 },
      { label: 'More than 12 months', sub: 'Highly secure financial foundation',          points: 4 },
    ],
  },
  {
    id: 'goal', category: 'Investment Goal', icon: '🎯',
    question: 'What best describes your primary investment objective?',
    context: "There's no right answer. Your goal shapes your entire strategy — and it can evolve as your life changes.",
    options: [
      { label: 'Preserve capital — avoid losses',      sub: 'Safety is the top priority',                     points: 0 },
      { label: 'Generate regular income (dividends)',  sub: 'Steady cash flow over growth',                   points: 1 },
      { label: 'Balanced growth and stability',        sub: 'Grow wealth without excessive risk',              points: 2 },
      { label: 'Long-term wealth accumulation',        sub: 'Maximize growth, accept volatility',              points: 3 },
      { label: 'Aggressive wealth building',           sub: 'Maximum growth, willing to accept large swings', points: 4 },
    ],
  },
  {
    id: 'scenario_crash', category: 'Scenario', icon: '📰',
    question: 'Breaking: economists predict a severe recession. Markets are falling. Your reaction?',
    context: "This tests your emotional response to scary financial headlines — a reliable predictor of actual investing behavior under stress.",
    options: [
      { label: 'Moving to cash immediately',     sub: 'Sell first, ask questions later',               points: 0 },
      { label: 'Rotating to safer assets',       sub: 'Shift to bonds and defensive stocks',           points: 1 },
      { label: 'Watch closely, hold for now',    sub: 'Recessions pass — staying the course',          points: 2 },
      { label: 'Rebalancing methodically',       sub: 'Disciplined — stick to the plan',               points: 3 },
      { label: 'Increasing investments',         sub: 'Recessions = best buying opportunities',        points: 4 },
    ],
  },
  {
    id: 'scenario_opportunity', category: 'Scenario', icon: '🚀',
    question: 'A trusted friend pitches a high-risk startup: could 5x in 2 years — or go to zero. You have $20k free. How much do you invest?',
    context: "This reveals your true comfort with speculative upside versus the real possibility of losing everything.",
    options: [
      { label: 'Nothing — way too risky', sub: "I can't afford to lose $20k",             points: 0 },
      { label: '$2,000 (10%)',            sub: 'Tiny exposure, mostly pass',               points: 1 },
      { label: '$5,000 (25%)',            sub: 'Meaningful bet while staying cautious',     points: 2 },
      { label: '$10,000 (50%)',           sub: 'High conviction, significant position',     points: 3 },
      { label: 'All $20,000',            sub: 'Max conviction — go big or go home',        points: 4 },
    ],
  },
];

const MAX_QUIZ_POINTS = QUESTIONS.reduce((a, q) => a + Math.max(...q.options.map(o => o.points)), 0);

const PROFILES = {
  conservative: {
    key: 'conservative', name: 'Conservative', tagline: 'Capital preservation with modest growth',
    scoreMin: 0, scoreMax: 20,
    color: '#3b82f6', bg: 'bg-blue-900/30', border: 'border-blue-700/40', text: 'text-blue-400',
    expectedReturn: 5.0, volatility: 8.0,
    description: "You prioritize protecting your wealth over chasing returns. A conservative portfolio of mostly bonds and cash equivalents helps you sleep at night — and that's a completely valid strategy for your situation.",
    allocation: { 'Large Cap US': 15, 'International': 5, 'Mid/Small Cap': 0, 'Bonds': 50, 'REITs': 5, 'Alternatives': 5, 'Sector': 0, 'Cash': 20 },
  },
  mod_conservative: {
    key: 'mod_conservative', name: 'Moderately Conservative', tagline: 'Stability with some growth potential',
    scoreMin: 21, scoreMax: 40,
    color: '#06b6d4', bg: 'bg-cyan-900/30', border: 'border-cyan-700/40', text: 'text-cyan-400',
    expectedReturn: 6.0, volatility: 11.0,
    description: "You want your money to grow but you're not willing to stomach big swings. A tilt toward bonds with meaningful equity exposure gives you growth potential with a safety net underneath.",
    allocation: { 'Large Cap US': 28, 'International': 8, 'Mid/Small Cap': 4, 'Bonds': 35, 'REITs': 8, 'Alternatives': 5, 'Sector': 0, 'Cash': 12 },
  },
  moderate: {
    key: 'moderate', name: 'Moderate', tagline: 'Balanced growth and stability',
    scoreMin: 41, scoreMax: 60,
    color: '#8b5cf6', bg: 'bg-purple-900/30', border: 'border-purple-700/40', text: 'text-purple-400',
    expectedReturn: 7.5, volatility: 14.0,
    description: "You're a classic balanced investor. You want your portfolio to grow meaningfully over time, but you're not willing to bet everything on equities. A modern 60/40 approach — with international diversification and alternatives — suits you well.",
    allocation: { 'Large Cap US': 38, 'International': 15, 'Mid/Small Cap': 7, 'Bonds': 22, 'REITs': 8, 'Alternatives': 5, 'Sector': 0, 'Cash': 5 },
  },
  mod_aggressive: {
    key: 'mod_aggressive', name: 'Moderately Aggressive', tagline: 'Growth-focused with downside protection',
    scoreMin: 61, scoreMax: 80,
    color: '#f59e0b', bg: 'bg-amber-900/30', border: 'border-amber-700/40', text: 'text-amber-400',
    expectedReturn: 9.0, volatility: 18.0,
    description: "You're comfortable with volatility and understand that short-term drops are the price of long-term wealth. A mostly-equity portfolio with a small bond buffer for opportunistic rebalancing suits your temperament.",
    allocation: { 'Large Cap US': 48, 'International': 20, 'Mid/Small Cap': 12, 'Bonds': 8, 'REITs': 7, 'Alternatives': 3, 'Sector': 0, 'Cash': 2 },
  },
  aggressive: {
    key: 'aggressive', name: 'Aggressive', tagline: 'Maximum long-term growth',
    scoreMin: 81, scoreMax: 100,
    color: '#10b981', bg: 'bg-emerald-900/30', border: 'border-emerald-700/40', text: 'text-emerald-400',
    expectedReturn: 10.5, volatility: 22.0,
    description: "You're a growth investor who understands that significant short-term losses are normal. You have a long time horizon, strong emotional resilience, and don't need to touch this money for decades. Full equity, globally diversified.",
    allocation: { 'Large Cap US': 55, 'International': 25, 'Mid/Small Cap': 12, 'Bonds': 0, 'REITs': 5, 'Alternatives': 3, 'Sector': 0, 'Cash': 0 },
  },
};

function getProfile(score) {
  if (score <= 20) return PROFILES.conservative;
  if (score <= 40) return PROFILES.mod_conservative;
  if (score <= 60) return PROFILES.moderate;
  if (score <= 80) return PROFILES.mod_aggressive;
  return PROFILES.aggressive;
}

// ─── Score gauge (SVG arc) ────────────────────────────────────────────────────
function ScoreGauge({ score, profile }) {
  const pct = score / 100;
  const cx = 100, cy = 85, r = 68;
  const toRad = d => (d * Math.PI) / 180;
  const startAngle = -150;
  const endAngle = startAngle + pct * 300;
  const sx = cx + r * Math.cos(toRad(startAngle));
  const sy = cy + r * Math.sin(toRad(startAngle));
  const ex = cx + r * Math.cos(toRad(endAngle));
  const ey = cy + r * Math.sin(toRad(endAngle));
  const fullEx = cx + r * Math.cos(toRad(150));
  const fullEy = cy + r * Math.sin(toRad(150));
  const largeArc = pct * 300 > 180 ? 1 : 0;

  return (
    <svg width="200" height="125" viewBox="0 0 200 125">
      <path d={`M ${cx + r * Math.cos(toRad(-150))} ${cy + r * Math.sin(toRad(-150))} A ${r} ${r} 0 1 1 ${fullEx} ${fullEy}`}
        fill="none" stroke="#334155" strokeWidth="12" strokeLinecap="round" />
      {pct > 0 && (
        <>
          <path d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
            fill="none" stroke={profile.color} strokeWidth="12" strokeLinecap="round" />
          <path d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
            fill="none" stroke={profile.color} strokeWidth="22" strokeLinecap="round" opacity="0.12" />
        </>
      )}
      <text x={cx} y={cy - 4}  textAnchor="middle" fill={profile.color} fontSize="30" fontWeight="900" fontFamily="system-ui">{score}</text>
      <text x={cx} y={cy + 16} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="system-ui">/100</text>
      <text x="18"  y="113" fill="#475569" fontSize="9" fontFamily="system-ui">Safe</text>
      <text x="155" y="113" fill="#475569" fontSize="9" fontFamily="system-ui">Bold</text>
    </svg>
  );
}

// ─── Single quiz question ─────────────────────────────────────────────────────
function QuizQuestion({ question, qIdx, total, onAnswer, onBack }) {
  const [selected, setSelected] = useState(null);

  const handleSelect = (opt) => {
    setSelected(opt.points);
    setTimeout(() => { onAnswer(opt.points); setSelected(null); }, 280);
  };

  const riskBar = (pts) => (
    <div className="flex gap-0.5 items-center flex-shrink-0">
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} className="w-1 h-3 rounded-sm transition-colors"
          style={{ background: i <= pts ? '#3b82f6' : '#334155' }} />
      ))}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Question {qIdx + 1} of {total}</span>
          <span className="text-slate-600 font-medium">{question.category}</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
          <div className="h-1.5 bg-blue-500 rounded-full transition-all duration-500"
            style={{ width: `${((qIdx + 1) / total) * 100}%` }} />
        </div>
      </div>

      {/* Card */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-5">
        <div className="text-4xl">{question.icon}</div>
        <div>
          <h2 className="text-lg font-bold text-slate-100 leading-snug mb-2">{question.question}</h2>
          {question.context && <p className="text-sm text-slate-400 leading-relaxed">{question.context}</p>}
        </div>

        <div className="space-y-2">
          {question.options.map((opt, i) => (
            <button key={i} onClick={() => handleSelect(opt)}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                selected === opt.points
                  ? 'bg-blue-600/25 border-blue-500/70 scale-[0.985]'
                  : 'bg-slate-700/30 border-slate-600/40 hover:bg-slate-700/60 hover:border-slate-500'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                  selected === opt.points ? 'border-blue-400 bg-blue-500' : 'border-slate-500'
                }`}>
                  {selected === opt.points && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-200">{opt.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{opt.sub}</div>
                </div>
                {riskBar(opt.points)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {qIdx > 0 && (
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">← Previous question</button>
      )}
    </div>
  );
}

// ─── Results page ─────────────────────────────────────────────────────────────
function RiskResults({ profile, score, mcResult, currentAllocation, totalValue, onRetake }) {
  const { userProfile } = useUserData();
  const recAlloc = profile.allocation;
  const userName = userProfile?.first_name || userProfile?.display_name || '';
  const recPieData = Object.entries(recAlloc)
    .filter(([, v]) => v > 0)
    .map(([name, pct]) => ({ name, value: pct, color: ASSET_COLORS[name] || '#64748b' }));

  const currentPcts = {};
  currentAllocation.forEach(a => { currentPcts[a.name] = (a.value / totalValue) * 100; });

  const allClasses = Object.keys(ASSET_COLORS);
  const rebalActions = allClasses
    .map(cls => ({ cls, rec: recAlloc[cls] || 0, cur: currentPcts[cls] || 0 }))
    .map(a => ({ ...a, diff: a.rec - a.cur, dollar: ((a.rec - a.cur) / 100) * totalValue }))
    .filter(a => Math.abs(a.diff) >= 2)
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

  const final = mcResult?.percentiles[mcResult.percentiles.length - 1];

  return (
    <div className="space-y-6">
      {/* Hero card */}
      <div className={`p-6 rounded-2xl border ${profile.bg} ${profile.border}`}>
        <div className="flex items-start gap-6 flex-wrap">
          <ScoreGauge score={score} profile={profile} />
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Your Risk Profile</div>
            <h2 className={`text-2xl font-bold ${profile.text} mb-0.5`}>{profile.name}</h2>
            <div className="text-sm text-slate-300 font-medium mb-3">{profile.tagline}</div>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">{profile.description}</p>
            <div className="flex items-center gap-5">
              <div>
                <div className={`text-xl font-bold ${profile.text} tabular-nums`}>{profile.expectedReturn}%</div>
                <div className="text-xs text-slate-500">Exp. return/yr</div>
              </div>
              <div className="w-px h-8 bg-slate-700" />
              <div>
                <div className="text-xl font-bold text-slate-300 tabular-nums">±{profile.volatility}%</div>
                <div className="text-xs text-slate-500">Annual volatility</div>
              </div>
              <div className="w-px h-8 bg-slate-700" />
              <div>
                <div className={`text-xl font-bold ${profile.text} tabular-nums`}>{fmtC(final?.p50 || 0)}</div>
                <div className="text-xs text-slate-500">Median 20yr value</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button onClick={onRetake}
              className="text-xs text-slate-400 hover:text-slate-200 bg-slate-700/50 hover:bg-slate-700 px-3 py-1.5 rounded-lg transition-colors">
              Retake →
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-700/50 hover:bg-slate-700 px-3 py-1.5 rounded-lg transition-colors"
            ><Printer size={11} /> Print</button>
            <button
              onClick={() => generateRiskReport({ profile, score, mcResult, totalValue, rebalActions, userName })}
              className="flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 bg-blue-900/20 hover:bg-blue-900/40 border border-blue-700/30 px-3 py-1.5 rounded-lg transition-colors"
            ><Download size={11} /> Download</button>
          </div>
        </div>
      </div>

      {/* Allocation + MC side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recommended allocation donut */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="font-semibold text-slate-200 text-sm mb-3">Optimal Allocation for Your Profile</h3>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie data={recPieData} cx="50%" cy="50%" innerRadius={48} outerRadius={78} paddingAngle={2} dataKey="value">
                {recPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                formatter={(v, n) => [`${v}%`, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
            {recPieData.map(a => (
              <div key={a.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: a.color }} />
                  <span className="text-slate-400">{a.name}</span>
                </div>
                <span className="font-bold text-slate-300 ml-2">{a.value}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monte Carlo for this profile */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="font-semibold text-slate-200 text-sm mb-0.5 flex items-center">
            Monte Carlo<InfoTooltip text={TOOLTIPS.monteCarlo} side="bottom" /> — Your Profile
          </h3>
          <p className="text-xs text-slate-500 mb-3">500 sims · 20 yr horizon · $2k/mo contributions</p>
          <div className="h-40">
            {mcResult && <InvestmentCanvas result={mcResult} years={20} />}
          </div>
          {final && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {[['Bear (P10)', final.p10, 'text-rose-400'], ['Median (P50)', final.p50, profile.text], ['Bull (P90)', final.p90, 'text-emerald-400']].map(([l, v, c]) => (
                <div key={l} className="text-center bg-slate-700/40 rounded-lg p-2.5">
                  <div className="text-xs text-slate-500 mb-0.5">{l}</div>
                  <div className={`text-sm font-bold tabular-nums ${c}`}>{fmtC(v)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Current vs Recommended + Rebalancing */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-5">
        <h3 className="font-semibold text-slate-200 text-sm">Current vs Recommended</h3>

        <div className="space-y-3">
          {allClasses.filter(cls => (recAlloc[cls] || 0) > 0 || (currentPcts[cls] || 0) > 0.5).map(cls => {
            const rec = recAlloc[cls] || 0;
            const cur = parseFloat((currentPcts[cls] || 0).toFixed(1));
            const color = ASSET_COLORS[cls] || '#64748b';
            const diff = rec - cur;
            return (
              <div key={cls}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-sm" style={{ background: color }} />
                    <span className="text-slate-300 font-medium">{cls}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    <span>Now <span className="text-slate-300 font-semibold">{cur}%</span></span>
                    <span>→</span>
                    <span>Target <span className="font-semibold" style={{ color }}>{rec}%</span></span>
                    {Math.abs(diff) >= 2 && (
                      <span className={`font-semibold tabular-nums ${diff > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        ({diff > 0 ? '+' : ''}{diff.toFixed(1)}%)
                      </span>
                    )}
                  </div>
                </div>
                <div className="relative h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div className="absolute inset-y-0 left-0 rounded-full opacity-35 transition-all"
                    style={{ width: `${Math.min(cur, 100)}%`, background: color }} />
                  <div className="absolute inset-y-0 left-0 rounded-full opacity-20 transition-all"
                    style={{ width: `${Math.min(rec, 100)}%`, background: color, border: `2px solid ${color}` }} />
                  {rec > 0 && <div className="absolute inset-y-0 w-0.5 bg-white/60 rounded-full"
                    style={{ left: `${Math.min(rec, 100)}%` }} />}
                </div>
              </div>
            );
          })}
        </div>

        {/* Rebalancing actions */}
        {rebalActions.length > 0 && (
          <div className="pt-2 border-t border-slate-700/60">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Suggested Rebalancing</div>
            <div className="space-y-3">
              {rebalActions.map(a => {
                const isSell = a.diff < 0;
                // Estimate tax only on sells (selling appreciated assets triggers gains)
                let taxInfo = null;
                if (isSell) {
                  const estimatedGain = Math.abs(a.dollar) * 0.30; // assume ~30% appreciation
                  const state = (userProfile?.stateOfResidence || '').toUpperCase().slice(0, 2);
                  const income = userProfile?.annualIncome || 100000;
                  const filing = userProfile?.taxFilingStatus || 'single';
                  taxInfo = estimateTax(estimatedGain, filing, income, state || 'CA');
                }
                return (
                  <div key={a.cls} className={`p-3 rounded-xl border text-xs ${
                    isSell ? 'bg-rose-950/30 border-rose-700/30' : 'bg-emerald-950/30 border-emerald-700/30'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`text-xl font-bold flex-shrink-0 ${isSell ? 'text-rose-400' : 'text-emerald-400'}`}>
                        {isSell ? '↓' : '↑'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold mb-0.5 ${isSell ? 'text-rose-300' : 'text-emerald-300'}`}>
                          {isSell ? 'Reduce' : 'Increase'} {a.cls}
                        </div>
                        <div className="text-slate-500 tabular-nums">
                          {a.cur.toFixed(1)}% → {a.rec}% ({a.diff > 0 ? '+' : ''}{a.diff.toFixed(1)}%)
                        </div>
                      </div>
                      <div className={`font-bold tabular-nums flex-shrink-0 ${isSell ? 'text-rose-400' : 'text-emerald-400'}`}>
                        {a.diff > 0 ? '+' : '-'}{fmt(Math.abs(a.dollar))}
                      </div>
                    </div>
                    {taxInfo && (
                      <div className="mt-2 ml-9 p-2 bg-slate-900/50 rounded-lg space-y-1 border border-slate-700/30">
                        <div className="text-slate-500 font-medium mb-1">Est. Tax on ~30% gain:</div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Federal LTCG ({taxInfo.ltcgRate.toFixed(1)}%)</span>
                          <span className="text-slate-300 tabular-nums">{fmt(taxInfo.fedTax)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">State ({(userProfile?.stateOfResidence||'').slice(0,2)||'CA'} {taxInfo.stateRate.toFixed(1)}%)</span>
                          <span className="text-slate-300 tabular-nums">{fmt(taxInfo.stateTax)}</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-700/40 pt-1 mt-1">
                          <span className="text-slate-400 font-semibold">Est. Total Tax</span>
                          <span className="text-rose-300 font-bold tabular-nums">{fmt(taxInfo.totalTax)}</span>
                        </div>
                        <div className="text-slate-600 mt-1">Tax-advantaged accounts (401k/Roth) rebalance tax-free.</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-start gap-2 p-3 bg-amber-950/30 border border-amber-700/30 rounded-xl text-xs text-amber-200/70 leading-relaxed">
              <Info size={11} className="text-amber-400 flex-shrink-0 mt-0.5" />
              Tax estimates are illustrative only and assume long-term capital gains on ~30% appreciated positions. Consult a licensed CPA or tax advisor before executing trades. Your actual tax liability will depend on your specific cost basis, holding periods, and other income.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Risk profile view (orchestrates landing → quiz → results) ────────────────
function RiskProfileView({ totalValue, allocation }) {
  const { investingData, updateSection } = useUserData();
  const [phase, setPhase] = useState('landing');
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  // Load saved risk profile from Supabase on mount
  useEffect(() => {
    const saved = investingData?.riskProfile;
    if (saved && saved.key && !result) {
      const profile = PROFILES[saved.key] || getProfile(saved.score || 50);
      const mcResult = runInvestmentMC({
        startValue: totalValue, monthlyContrib: 2000,
        annualReturn: profile.expectedReturn, annualVol: profile.volatility, years: 20, numSims: 1000,
      });
      setResult({ profile, score: saved.score || 50, mcResult });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswer = (points) => {
    const newAnswers = [...answers, points];
    if (qIdx < QUESTIONS.length - 1) {
      setAnswers(newAnswers);
      setQIdx(qIdx + 1);
    } else {
      const total = newAnswers.reduce((s, p) => s + p, 0);
      const score = Math.round((total / MAX_QUIZ_POINTS) * 100);
      const profile = getProfile(score);
      const mcResult = runInvestmentMC({
        startValue: totalValue, monthlyContrib: 2000,
        annualReturn: profile.expectedReturn, annualVol: profile.volatility, years: 20, numSims: 1000,
      });
      const newResult = { profile, score, mcResult };
      setResult(newResult);
      setPhase('results');
      // Save to Supabase
      updateSection('investing', {
        ...investingData,
        riskProfile: { key: profile.key, score, timestamp: Date.now() },
      });
    }
  };

  const handleBack = () => {
    if (qIdx === 0) { setPhase('landing'); setAnswers([]); }
    else { setQIdx(qIdx - 1); setAnswers(answers.slice(0, -1)); }
  };

  const resetAll = () => { setPhase('quiz'); setQIdx(0); setAnswers([]); setResult(null); };

  const savedProfile = investingData?.riskProfile;

  if (phase === 'landing') return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center space-y-5">
        <div className="text-5xl">🧭</div>
        <div>
          <h2 className="text-xl font-bold text-slate-100 mb-1">Risk Tolerance Assessment</h2>
          <p className="text-slate-500 text-sm">8 questions · ~2 minutes · Personalized results</p>
        </div>
        {savedProfile && (
          <div className="flex items-center justify-center gap-2 text-xs text-emerald-300 bg-emerald-900/20 border border-emerald-700/30 rounded-lg px-3 py-2">
            <span>✓</span>
            <span>Saved profile: <strong>{PROFILES[savedProfile.key]?.name || 'Unknown'}</strong> · Score {savedProfile.score} · Saved {new Date(savedProfile.timestamp).toLocaleDateString()}</span>
          </div>
        )}
        <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto">
          Answer honestly based on how you actually think and feel about money, risk, and your financial situation. We'll calculate your risk profile and show you the optimal allocation — then run a Monte Carlo to project your 20-year outcomes.
        </p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: '📊', label: 'Risk Score', desc: 'Personalized 0–100 rating' },
            { icon: '🥧', label: 'Optimal Allocation', desc: 'Ideal asset mix for you' },
            { icon: '📈', label: 'Monte Carlo', desc: 'Projected 20-year outcomes' },
          ].map(f => (
            <div key={f.label} className="bg-slate-700/40 rounded-xl p-3">
              <div className="text-2xl mb-1">{f.icon}</div>
              <div className="text-xs font-semibold text-slate-300">{f.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{f.desc}</div>
            </div>
          ))}
        </div>
        {result && (
          <button onClick={() => setPhase('results')}
            className="w-full bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium py-2.5 rounded-xl transition-colors">
            View Results — <span className={result.profile.text}>{result.profile.name}</span> (Score: {result.score})
          </button>
        )}
        <button onClick={() => setPhase('quiz')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm">
          {result ? 'Retake Assessment →' : 'Start Assessment →'}
        </button>
        <p className="text-xs text-slate-600">For educational purposes only · Not financial advice</p>
      </div>
    </div>
  );

  if (phase === 'quiz') return (
    <QuizQuestion question={QUESTIONS[qIdx]} qIdx={qIdx} total={QUESTIONS.length} onAnswer={handleAnswer} onBack={handleBack} />
  );

  if (phase === 'results' && result) return (
    <RiskResults profile={result.profile} score={result.score} mcResult={result.mcResult}
      currentAllocation={allocation} totalValue={totalValue} onRetake={resetAll} />
  );

  return null;
}

// ─── Risk Stats view ─────────────────────────────────────────────────────────
function RiskStatsView({ allocation, totalValue }) {
  const stats = computePortfolioStats(allocation, totalValue);

  if (!allocation || allocation.length === 0 || !stats) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="text-4xl mb-4">📊</div>
        <p className="text-slate-400 text-sm">No holdings data available to compute risk statistics.</p>
        <p className="text-slate-500 text-xs mt-2">Add holdings to your portfolio first.</p>
      </div>
    );
  }

  const { portReturn, portVol, beta, sharpe, var95, maxDD } = stats;

  const sharpeColor = sharpe >= 1 ? 'text-emerald-400' : sharpe >= 0.5 ? 'text-amber-400' : 'text-rose-400';
  const sharpeBg    = sharpe >= 1 ? 'border-emerald-700/30 bg-emerald-950/20' : sharpe >= 0.5 ? 'border-amber-700/30 bg-amber-950/20' : 'border-rose-700/30 bg-rose-950/20';
  const betaColor   = Math.abs(beta - 1) < 0.15 ? 'text-blue-400' : beta > 1.2 ? 'text-amber-400' : 'text-slate-300';
  const volColor    = portVol < 12 ? 'text-emerald-400' : portVol < 20 ? 'text-amber-400' : 'text-rose-400';

  const metricCards = [
    {
      label: 'Annualized Return',
      value: `${portReturn.toFixed(1)}%`,
      color: 'text-emerald-400',
      border: 'border-emerald-700/30 bg-emerald-950/20',
      desc: 'Weighted average expected return based on asset class historical averages.',
      tooltip: TOOLTIPS.annualizedReturn,
    },
    {
      label: 'Volatility (σ)',
      value: `${portVol.toFixed(1)}%`,
      color: volColor,
      border: portVol < 12 ? 'border-emerald-700/30 bg-emerald-950/20' : portVol < 20 ? 'border-amber-700/30 bg-amber-950/20' : 'border-rose-700/30 bg-rose-950/20',
      desc: 'Annual standard deviation of portfolio returns. Lower = smoother ride.',
      tooltip: TOOLTIPS.standardDeviation,
    },
    {
      label: 'Sharpe Ratio',
      value: sharpe.toFixed(2),
      color: sharpeColor,
      border: sharpeBg,
      desc: 'Risk-adjusted return (return minus 5% risk-free rate, divided by vol). >1 is excellent.',
      tooltip: TOOLTIPS.sharpeRatio,
    },
    {
      label: '1-yr VaR (95%)',
      value: fmt(var95),
      color: 'text-rose-400',
      border: 'border-rose-700/30 bg-rose-950/20',
      desc: 'At 95% confidence, your portfolio should not lose more than this amount in one year.',
      tooltip: TOOLTIPS.valueAtRisk,
    },
    {
      label: 'Max Drawdown Est.',
      value: `~${maxDD.toFixed(0)}%`,
      color: 'text-amber-400',
      border: 'border-amber-700/30 bg-amber-950/20',
      desc: 'Rough estimate of worst-case peak-to-trough decline based on historical vol.',
      tooltip: TOOLTIPS.maxDrawdown,
    },
    {
      label: 'Beta vs S&P 500',
      value: beta.toFixed(2),
      color: betaColor,
      border: Math.abs(beta - 1) < 0.15 ? 'border-blue-700/30 bg-blue-950/20' : beta > 1.2 ? 'border-amber-700/30 bg-amber-950/20' : 'border-slate-700/30 bg-slate-800',
      desc: 'Portfolio sensitivity to S&P 500 moves. 1.0 = moves in lockstep with the market.',
      tooltip: TOOLTIPS.beta,
    },
  ];

  return (
    <div className="space-y-6">
      {/* 6 stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {metricCards.map(c => (
          <div key={c.label} className={`p-4 rounded-xl border ${c.border}`}>
            <div className="text-xs text-slate-500 mb-1.5 flex items-center">
              {c.label}
              {c.tooltip && <InfoTooltip text={c.tooltip} side="bottom" />}
            </div>
            <div className={`text-2xl font-bold tabular-nums ${c.color} mb-2`}>{c.value}</div>
            <div className="text-xs text-slate-600 leading-relaxed">{c.desc}</div>
          </div>
        ))}
      </div>

      {/* Portfolio composition factor table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 overflow-x-auto">
        <h2 className="font-semibold text-slate-200 text-sm mb-4">Portfolio Composition Factors</h2>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-500 border-b border-slate-700">
              <th className="text-left pb-2 font-medium">Asset Class</th>
              <th className="text-right pb-2 font-medium">Weight</th>
              <th className="text-right pb-2 font-medium">Exp. Return</th>
              <th className="text-right pb-2 font-medium">
                <span className="inline-flex items-center justify-end gap-0.5">
                  Volatility<InfoTooltip text={TOOLTIPS.standardDeviation} side="bottom" />
                </span>
              </th>
              <th className="text-right pb-2 font-medium">
                <span className="inline-flex items-center justify-end gap-0.5">
                  Beta<InfoTooltip text={TOOLTIPS.beta} side="bottom" />
                </span>
              </th>
              <th className="text-right pb-2 font-medium">Return Contrib.</th>
            </tr>
          </thead>
          <tbody>
            {allocation.map(a => {
              const s = ASSET_CLASS_STATS[a.name];
              if (!s) return null;
              const w = a.value / totalValue;
              return (
                <tr key={a.name} className="border-b border-slate-700/40 hover:bg-slate-700/20 transition-colors">
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: ASSET_COLORS[a.name] || '#64748b' }} />
                      <span className="text-slate-300 font-medium">{a.name}</span>
                    </div>
                  </td>
                  <td className="py-2.5 text-right tabular-nums text-slate-400">{(w * 100).toFixed(1)}%</td>
                  <td className="py-2.5 text-right tabular-nums text-emerald-400">{s.return}%</td>
                  <td className="py-2.5 text-right tabular-nums text-amber-400">{s.vol}%</td>
                  <td className="py-2.5 text-right tabular-nums text-blue-400">{s.beta.toFixed(2)}</td>
                  <td className="py-2.5 text-right tabular-nums text-slate-300">{(w * s.return).toFixed(2)}%</td>
                </tr>
              );
            })}
            <tr className="border-t border-slate-600">
              <td className="py-2.5 font-bold text-slate-200">Portfolio Total</td>
              <td className="py-2.5 text-right tabular-nums font-bold text-slate-200">100%</td>
              <td className="py-2.5 text-right tabular-nums font-bold text-emerald-400">{portReturn.toFixed(1)}%</td>
              <td className="py-2.5 text-right tabular-nums font-bold text-amber-400">{portVol.toFixed(1)}%</td>
              <td className="py-2.5 text-right tabular-nums font-bold text-blue-400">{beta.toFixed(2)}</td>
              <td className="py-2.5 text-right tabular-nums font-bold text-slate-200">{portReturn.toFixed(2)}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 p-4 bg-slate-700/30 border border-slate-600/40 rounded-xl text-xs text-slate-400 leading-relaxed">
        <Info size={12} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <span>
          All figures are estimates based on historical asset class averages and standard portfolio theory assumptions.
          Diversification benefit is approximated with a 1.3× correlation scale factor.
          Past performance does not guarantee future results. These statistics are for educational purposes only and do not constitute investment advice.
        </span>
      </div>
    </div>
  );
}

// ── Setup form component ──────────────────────────────────────────────────────
function SetupForm({ onSetup }) {
  const [totalValue, setTotalValue] = useState('');
  const [dayChange, setDayChange] = useState('');
  const [totalReturn, setTotalReturn] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const tv = parseFloat(totalValue) || 0;
    const dc = parseFloat(dayChange) || 0;
    const tr = parseFloat(totalReturn) || 0;
    if (tv <= 0) return;
    onSetup({
      totalValue: tv,
      dayChange: dc,
      dayChangePct: tv > 0 ? (dc / (tv - dc)) * 100 : 0,
      totalReturn: tr,
      totalReturnPct: tv > 0 ? (tr / (tv - tr)) * 100 : 0,
      linkedAccounts: [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Total Portfolio Value</label>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">$</span>
          <input
            value={totalValue}
            onChange={e => setTotalValue(e.target.value)}
            placeholder="0"
            type="number"
            min="0"
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            autoFocus
            required
          />
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Today's Gain/Loss (optional)</label>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">$</span>
          <input
            value={dayChange}
            onChange={e => setDayChange(e.target.value)}
            placeholder="0"
            type="number"
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Total Return (optional)</label>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">$</span>
          <input
            value={totalReturn}
            onChange={e => setTotalReturn(e.target.value)}
            placeholder="0"
            type="number"
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>
      <div className="flex gap-3 pt-1">
        <button
          type="submit"
          className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
        >
          Set Up Portfolio
        </button>
      </div>
    </form>
  );
}

// ─── Generic delete-confirmation dialog ───────────────────────────────────────
function DeleteConfirmDialog({ title, body, confirmLabel = 'Delete', onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-10 bg-rose-900/40 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={18} className="text-rose-400" />
        </div>
        <h3 className="font-bold text-slate-100 text-base mb-1">{title}</h3>
        <p className="text-sm text-slate-400 mb-6 leading-relaxed">{body}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">Cancel</button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold rounded-lg transition-colors">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ─── EditAccountModal — pre-filled edit for institution / nickname / balance ──
function EditAccountModal({ account, onClose, onSave }) {
  const [institution, setInstitution] = useState(account.institution || '');
  const [nickname,    setNickname]    = useState(account.nickname || account.name || '');
  const [balance,     setBalance]     = useState(account.balance != null ? String(account.balance) : '');

  const handleSave = () => {
    const updated = {
      ...account,
      institution: institution.trim(),
      name: nickname.trim() || institution.trim() || account.name,
      nickname: nickname.trim(),
      balance: parseFloat(balance) || account.balance || 0,
    };
    onSave(updated);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">Edit Account</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block font-medium">Institution</label>
            <input value={institution} onChange={e => setInstitution(e.target.value)} autoFocus
              placeholder="e.g. Vanguard, Fidelity"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block font-medium">Nickname <span className="text-slate-600">(optional)</span></label>
            <input value={nickname} onChange={e => setNickname(e.target.value)}
              placeholder="e.g. My Roth IRA"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block font-medium">Current Balance</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input value={balance} onChange={e => setBalance(e.target.value)}
                type="number" min="0" step="0.01" placeholder="0.00"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-7 pr-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">Cancel</button>
          <button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// ─── EditHoldingModal — edit / add purchase lots for a holding ────────────────
function EditHoldingModal({ holding, accountName, onClose, onSave }) {
  // Seed with existing lots, or migrate from legacy flat fields.
  // costBasisPerShare in legacy data actually stored the TOTAL the user typed
  // (the field was mislabeled), so totalAmountPaid = costBasisPerShare, not ×shares.
  const seedLots = () => {
    if (holding.lots && holding.lots.length > 0) {
      return holding.lots.map(l => ({
        ...l,
        id:             l.id || `lot_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        shares:         String(l.shares          ?? ''),
        // migrate old pricePaid (per-share, entered as total) → totalAmountPaid
        totalAmountPaid: l.totalAmountPaid != null
          ? String(l.totalAmountPaid)
          : String(l.pricePaid ?? ''),
      }));
    }
    // Legacy holding with no lots: costBasisPerShare stored the total the user entered
    return [{
      ...newLotRow(),
      shares:         String(holding.shares            ?? ''),
      totalAmountPaid: String(holding.costBasisPerShare ?? ''),
    }];
  };

  const [lots, setLots] = useState(seedLots);

  const addLot    = () => setLots(l => [...l, newLotRow()]);
  const removeLot = (id) => { if (lots.length > 1) setLots(l => l.filter(x => x.id !== id)); };
  const updateLot = (id, field, val) => setLots(l => l.map(x => x.id === id ? { ...x, [field]: val } : x));

  const { totalShares, totalCost, avgCost } = computeFromLots(lots);
  const hasCost = avgCost !== null;

  const handleSave = () => {
    const validLots = lots
      .filter(l => parseFloat(l.shares) > 0)
      .map(l => ({ ...l, shares: parseFloat(l.shares), totalAmountPaid: parseFloat(l.totalAmountPaid) || null }));
    onSave({
      ...holding,
      lots:              validLots,
      shares:            totalShares,
      costBasisPerShare: avgCost,        // weighted avg = totalCost ÷ totalShares
      costBasisTotal:    hasCost ? totalCost : null,   // sum of totalAmountPaid
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700 flex-shrink-0">
          <div>
            <h3 className="font-bold text-slate-100 text-base">{holding.ticker}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{accountName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Purchase History lots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-400 font-medium">Purchase History</label>
              <span className="text-xs text-slate-600">Edit lots or add a new purchase</span>
            </div>
            <div className="grid gap-2 px-1 mb-1.5 text-xs text-slate-600 font-medium"
              style={{ gridTemplateColumns: '1fr 0.75fr 1fr 20px' }}>
              <div>Date Purchased</div>
              <div>Shares</div>
              <div>Total Amount Paid</div>
              <div />
            </div>
            <p className="text-xs text-slate-600 mb-2">Total dollars you paid for all shares in each lot — avg cost/share is calculated automatically.</p>
            <div className="space-y-2">
              {lots.map((lot) => (
                <div key={lot.id} className="grid gap-2 items-center"
                  style={{ gridTemplateColumns: '1fr 0.75fr 1fr 20px' }}>
                  <input value={lot.date} onChange={e => updateLot(lot.id, 'date', e.target.value)}
                    type="date"
                    className="bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-xs focus:outline-none focus:border-blue-500 w-full" />
                  <input value={lot.shares} onChange={e => updateLot(lot.id, 'shares', e.target.value)}
                    type="number" min="0" step="any" placeholder="0"
                    className="bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-xs focus:outline-none focus:border-blue-500 w-full" />
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                    <input value={lot.totalAmountPaid} onChange={e => updateLot(lot.id, 'totalAmountPaid', e.target.value)}
                      type="number" min="0" step="0.01" placeholder="0.00"
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-6 pr-2.5 py-2 text-slate-100 text-xs focus:outline-none focus:border-blue-500" />
                  </div>
                  <button onClick={() => removeLot(lot.id)} disabled={lots.length === 1}
                    className="flex items-center justify-center text-slate-600 hover:text-rose-400 disabled:pointer-events-none disabled:opacity-0 transition-colors">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addLot}
              className="mt-2.5 w-full py-2 border border-dashed border-slate-600 text-slate-500 hover:text-slate-300 hover:border-slate-400 rounded-lg text-xs transition-colors flex items-center justify-center gap-1.5">
              <Plus size={12} /> Add another lot
            </button>
          </div>

          {/* Live summary */}
          {totalShares > 0 && (
            <div className="grid grid-cols-3 gap-3 p-3 bg-slate-700/40 rounded-xl border border-slate-600/40">
              <div className="text-center">
                <div className="text-xs text-slate-500 mb-0.5">Total Shares</div>
                <div className="text-sm font-bold text-slate-200 tabular-nums">{totalShares.toLocaleString(undefined, { maximumFractionDigits: 6 })}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500 mb-0.5">Avg. Cost / Share</div>
                <div className="text-sm font-bold text-slate-200 tabular-nums">{hasCost ? `$${avgCost.toFixed(2)}` : '—'}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500 mb-0.5">Total Cost Basis</div>
                <div className="text-sm font-bold text-slate-200 tabular-nums">{hasCost ? fmt(totalCost) : '—'}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-700 flex-shrink-0 flex gap-3">
          <button onClick={onClose} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">Cancel</button>
          <button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── ManualInvestmentModal — 3-step account + holdings entry ─────────────────
function ManualInvestmentModal({ onClose, onSave }) {
  const [step,        setStep]        = useState(1);
  const [accountType, setAccountType] = useState('');
  const [institution, setInstitution] = useState('');
  const [nickname,    setNickname]    = useState('');
  const [balance,     setBalance]     = useState('');
  const [rows, setRows] = useState([
    { id: 1, ticker: '', name: '', shares: '', costBasis: '' },
  ]);

  const addRow    = () => setRows(r => [...r, { id: Date.now(), ticker: '', name: '', shares: '', costBasis: '' }]);
  const removeRow = (id) => setRows(r => r.filter(x => x.id !== id));
  const updateRow = (id, field, value) =>
    setRows(r => r.map(x => x.id === id ? { ...x, [field]: value } : x));

  const buildAccount = (skipHoldings = false) => {
    const holdings = skipHoldings ? [] : rows
      .filter(r => r.ticker.trim())
      .map(r => {
        const upper  = r.ticker.toUpperCase().trim();
        const shares = parseFloat(r.shares)    || 0;
        const cb     = parseFloat(r.costBasis) || 0;
        // cb is treated as total amount paid (not per share)
        const lots = cb > 0 ? [{
          id: `lot_${upper}_${Date.now()}`,
          date: new Date().toISOString().slice(0, 10),
          shares,
          totalAmountPaid: cb,
        }] : [];
        const avgCostPerShare = cb > 0 && shares > 0 ? cb / shares : null;
        return {
          id:                `h_${upper}_${Date.now()}`,
          ticker:            upper,
          name:              r.name.trim() || upper,
          lots,
          shares,
          currentPrice:      null,          // fetched live via Alpha Vantage on load
          costBasisPerShare: avgCostPerShare,  // computed avg = total ÷ shares
          value:             0,             // computed from live price after load
          costBasisTotal:    cb > 0 ? cb : null,   // total amount paid
          assetClass:        TICKER_ASSET_CLASS[upper] || inferAssetClassFromType(accountType),
          dayChangePct:      0,
        };
      });
    const bal = parseFloat(balance) || 0;
    return {
      id:                  `inv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      connectionType:      'manual',
      isInvestmentAccount: true,
      type:                accountType,
      name:                nickname.trim() || institution.trim() ||
                           (ACCOUNT_TYPE_OPTIONS.find(o => o.id === accountType)?.label ?? 'Investment Account'),
      institution:         institution.trim(),
      balance:             bal,
      currency:            'USD',
      holdings,
      addedAt:             new Date().toISOString(),
    };
  };

  const canGoStep2 = !!accountType;
  const canGoStep3 = institution.trim().length > 0 && balance !== '';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700 flex-shrink-0">
          <div>
            <h3 className="font-bold text-slate-100 text-lg">Add Investment Account</h3>
            <div className="flex items-center gap-2 mt-1.5">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-1 rounded-full transition-all ${s <= step ? 'bg-blue-500 w-8' : 'bg-slate-600 w-4'}`} />
              ))}
              <span className="text-xs text-slate-500 ml-1">Step {step} of 3</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* ── Step 1: Account Type ── */}
          {step === 1 && (
            <>
              <p className="text-sm text-slate-400">What type of investment account is this?</p>
              <div className="grid grid-cols-2 gap-3">
                {ACCOUNT_TYPE_OPTIONS.map(opt => (
                  <button key={opt.id} onClick={() => setAccountType(opt.id)}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all ${
                      accountType === opt.id
                        ? 'border-blue-500 bg-blue-900/30 text-slate-100'
                        : 'border-slate-600/60 bg-slate-700/30 text-slate-300 hover:bg-slate-700/60 hover:border-slate-500'
                    }`}
                  >
                    <span className="text-xl">{opt.icon}</span>
                    <span className="text-sm font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ── Step 2: Account Details ── */}
          {step === 2 && (
            <>
              <p className="text-sm text-slate-400">Enter your account details.</p>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block font-medium">Institution <span className="text-rose-400">*</span></label>
                <input value={institution} onChange={e => setInstitution(e.target.value)} autoFocus
                  placeholder="e.g. Vanguard, Fidelity, Robinhood"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block font-medium">Nickname <span className="text-slate-600">(optional)</span></label>
                <input value={nickname} onChange={e => setNickname(e.target.value)}
                  placeholder="e.g. My Roth IRA"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block font-medium">Current Balance <span className="text-rose-400">*</span></label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input value={balance} onChange={e => setBalance(e.target.value)}
                    placeholder="0.00" type="number" min="0" step="0.01"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-7 pr-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* ── Step 3: Holdings ── */}
          {step === 3 && (
            <>
              <p className="text-sm text-slate-400">
                Add individual holdings to track performance and gain/loss.{' '}
                <span className="text-slate-500">You can skip and add them later.</span>
              </p>
              <div className="space-y-3">
                {rows.map((row, i) => {
                  const upper = row.ticker.toUpperCase();
                  const inferredClass = TICKER_ASSET_CLASS[upper] || inferAssetClassFromType(accountType);
                  return (
                    <div key={row.id} className="p-3 bg-slate-700/40 border border-slate-600/40 rounded-xl space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 font-medium">Holding {i + 1}</span>
                        {rows.length > 1 && (
                          <button onClick={() => removeRow(row.id)} className="text-rose-400/60 hover:text-rose-400 transition-colors">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Ticker / Symbol</label>
                          <input value={row.ticker} onChange={e => updateRow(row.id, 'ticker', e.target.value)}
                            placeholder="VOO, AAPL, BTC…"
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-sm font-mono uppercase focus:outline-none focus:border-blue-500 placeholder-slate-500 placeholder:normal-case"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Asset Name <span className="text-slate-600">(opt.)</span></label>
                          <input value={row.name} onChange={e => updateRow(row.id, 'name', e.target.value)}
                            placeholder="e.g. S&P 500 ETF"
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Shares</label>
                          <input value={row.shares} onChange={e => updateRow(row.id, 'shares', e.target.value)}
                            placeholder="0" type="number" min="0" step="any"
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Total amount paid <span className="text-slate-600">(opt.)</span></label>
                          <input value={row.costBasis} onChange={e => updateRow(row.id, 'costBasis', e.target.value)}
                            placeholder="0.00" type="number" min="0" step="0.01"
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                      {upper && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="text-slate-600">{inferredClass}</span>
                          <span className="text-slate-700">·</span>
                          <span className="text-blue-500/70">Price fetched automatically on save</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button onClick={addRow}
                className="w-full py-2.5 border border-dashed border-slate-600 text-slate-400 hover:text-slate-200 hover:border-slate-400 rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={14} /> Add another holding
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-700 flex-shrink-0">
          {step === 1 && (
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">Cancel</button>
              <button onClick={() => setStep(2)} disabled={!canGoStep2}
                className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
              >Next →</button>
            </div>
          )}
          {step === 2 && (
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">← Back</button>
              <button onClick={() => setStep(3)} disabled={!canGoStep3}
                className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
              >Next →</button>
            </div>
          )}
          {step === 3 && (
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">← Back</button>
              <button onClick={() => { onSave(buildAccount(true)); onClose(); }}
                className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors"
              >Skip</button>
              <button onClick={() => { onSave(buildAccount(false)); onClose(); }}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
              >Save Account</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Lot helpers ──────────────────────────────────────────────────────────────
function newLotRow() {
  return {
    id: `lot_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    date: new Date().toISOString().slice(0, 10),
    shares: '',
    totalAmountPaid: '',   // total dollars paid for all shares in this lot
  };
}

function computeFromLots(lots) {
  const totalShares = lots.reduce((s, l) => s + (parseFloat(l.shares) || 0), 0);
  // totalAmountPaid is the dollar total for the lot, NOT per share
  const totalCost   = lots.reduce((s, l) => s + (parseFloat(l.totalAmountPaid) || 0), 0);
  const avgCost     = totalShares > 0 && totalCost > 0 ? totalCost / totalShares : null;
  return { totalShares, totalCost, avgCost };
}

// ─── AddHoldingModal — quick-add a holding with purchase lots ─────────────────
function AddHoldingModal({ accounts, onClose, onSave }) {
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const [ticker,    setTicker]    = useState('');
  const [name,      setName]      = useState('');
  const [lots,      setLots]      = useState([newLotRow()]);

  const addLot    = () => setLots(l => [...l, newLotRow()]);
  const removeLot = (id) => setLots(l => l.filter(x => x.id !== id));
  const updateLot = (id, field, val) => setLots(l => l.map(x => x.id === id ? { ...x, [field]: val } : x));

  const upper = ticker.toUpperCase().trim();
  const { totalShares, totalCost, avgCost } = computeFromLots(lots);
  const hasCost = avgCost !== null;

  const handleSave = () => {
    if (!upper || totalShares <= 0) return;
    const validLots = lots
      .filter(l => parseFloat(l.shares) > 0)
      .map(l => ({ ...l, shares: parseFloat(l.shares), totalAmountPaid: parseFloat(l.totalAmountPaid) || null }));
    onSave(accountId, {
      id:                `h_${upper}_${Date.now()}`,
      ticker:            upper,
      name:              name.trim() || upper,
      lots:              validLots,
      shares:            totalShares,
      costBasisPerShare: avgCost,        // weighted avg = totalCost ÷ totalShares
      costBasisTotal:    hasCost ? totalCost : null,   // sum of totalAmountPaid
      assetClass:        TICKER_ASSET_CLASS[upper] || 'Large Cap US',
      dayChangePct:      0,
      isManual:          true,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700 flex-shrink-0">
          <h3 className="font-bold text-slate-100 text-lg">Add Holding</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {accounts.length > 1 && (
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block font-medium">Account</label>
              <select value={accountId} onChange={e => setAccountId(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500">
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          {/* Ticker + Name */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block font-medium">Ticker</label>
              <input value={ticker} onChange={e => setTicker(e.target.value)} placeholder="VOO" autoFocus
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm font-mono uppercase focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block font-medium">Asset Name <span className="text-slate-600">(opt.)</span></label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="S&P 500 ETF"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500" />
            </div>
          </div>

          {/* Purchase History lots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-400 font-medium">Purchase History</label>
              <span className="text-xs text-slate-600">Each time you bought this asset</span>
            </div>
            {/* Column headers */}
            <div className="grid gap-2 px-1 mb-1.5 text-xs text-slate-600 font-medium"
              style={{ gridTemplateColumns: '1fr 0.75fr 1fr 20px' }}>
              <div>Date Purchased</div>
              <div>Shares</div>
              <div>Total Amount Paid</div>
              <div />
            </div>
            <p className="text-xs text-slate-600 mb-2">How much did you pay in total for all shares in this lot?</p>
            <div className="space-y-2">
              {lots.map((lot) => (
                <div key={lot.id} className="grid gap-2 items-center"
                  style={{ gridTemplateColumns: '1fr 0.75fr 1fr 20px' }}>
                  <input value={lot.date} onChange={e => updateLot(lot.id, 'date', e.target.value)}
                    type="date"
                    className="bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-xs focus:outline-none focus:border-blue-500 w-full" />
                  <input value={lot.shares} onChange={e => updateLot(lot.id, 'shares', e.target.value)}
                    type="number" min="0" step="any" placeholder="0"
                    className="bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-xs focus:outline-none focus:border-blue-500 w-full" />
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                    <input value={lot.totalAmountPaid} onChange={e => updateLot(lot.id, 'totalAmountPaid', e.target.value)}
                      type="number" min="0" step="0.01" placeholder="0.00"
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-6 pr-2.5 py-2 text-slate-100 text-xs focus:outline-none focus:border-blue-500" />
                  </div>
                  <button onClick={() => removeLot(lot.id)} disabled={lots.length === 1}
                    className="flex items-center justify-center text-slate-600 hover:text-rose-400 disabled:pointer-events-none disabled:opacity-0 transition-colors">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addLot}
              className="mt-2.5 w-full py-2 border border-dashed border-slate-600 text-slate-500 hover:text-slate-300 hover:border-slate-400 rounded-lg text-xs transition-colors flex items-center justify-center gap-1.5">
              <Plus size={12} /> Add another lot
            </button>
          </div>

          {/* Live summary */}
          {totalShares > 0 && (
            <div className="grid grid-cols-3 gap-3 p-3 bg-slate-700/40 rounded-xl border border-slate-600/40">
              <div className="text-center">
                <div className="text-xs text-slate-500 mb-0.5">Total Shares</div>
                <div className="text-sm font-bold text-slate-200 tabular-nums">{totalShares.toLocaleString(undefined, { maximumFractionDigits: 6 })}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500 mb-0.5">Avg. Cost / Share</div>
                <div className="text-sm font-bold text-slate-200 tabular-nums">{hasCost ? `$${avgCost.toFixed(2)}` : '—'}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-500 mb-0.5">Total Cost Basis</div>
                <div className="text-sm font-bold text-slate-200 tabular-nums">{hasCost ? fmt(totalCost) : '—'}</div>
              </div>
            </div>
          )}

          {upper && (
            <p className="text-xs text-slate-600">
              Current market price fetched automatically — no need to enter it manually.
              {TICKER_ASSET_CLASS[upper] && <span className="ml-2 text-slate-700">{TICKER_ASSET_CLASS[upper]}</span>}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-700 flex-shrink-0 flex gap-3">
          <button onClick={handleSave} disabled={!upper || totalShares <= 0}
            className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
            Add Holding
          </button>
          <button onClick={onClose} className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Investing component ─────────────────────────────────────────────────
export default function Investing() {
  const { investingData, updateSection, linkedAccountsData } = useUserData();
  const [showSetup,   setShowSetup]   = useState(false);
  const [view,        setView]        = usePersistentState('finovo_investing_view', 'overview');
  const [filterType,  setFilterType]  = useState('all');  // 'all' | account type id

  // ── Manual investment accounts from user profile ───────────────────────────
  // (These hooks must run unconditionally, before any early return)
  const manualInvestmentAccounts = useMemo(
    () => (linkedAccountsData || []).filter(a => a.isInvestmentAccount),
    [linkedAccountsData]
  );

  // ── All tickers for Alpha Vantage price fetching ──────────────────────────
  const allTickers = useMemo(() =>
    manualInvestmentAccounts.flatMap(a => (a.holdings || []).map(h => h.ticker).filter(Boolean)),
    [manualInvestmentAccounts]
  );
  const { prices: livePrices, quotes: liveQuotes, priceDate } = useLivePrices(allTickers);

  // ── Build all holdings with live prices injected ──────────────────────────
  // Computed directly (not via useMemo) so livePrices are always applied on
  // every render — avoids stale-memo issues when setPrices fires asynchronously.
  const allHoldingsUnfiltered = buildAllHoldings(
    investingData || { linkedAccounts: [], totalValue: 0 },
    manualInvestmentAccounts,
    livePrices
  );

  // ── Account-type tabs (dynamic — only show types user has) ────────────────
  const accountTypesPresent = useMemo(() => {
    const types = new Set(manualInvestmentAccounts.map(a => a.type).filter(Boolean));
    return ACCOUNT_TYPE_OPTIONS.filter(opt => types.has(opt.id));
  }, [manualInvestmentAccounts]);

  // ── Setup modal (shown when no investing data exists yet) ──────────────────
  if (!investingData) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        {showSetup && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSetup(false)}>
            <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-slate-100 text-lg">Set Up Investment Portfolio</h3>
                <button onClick={() => setShowSetup(false)} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
                  <X size={16} className="text-slate-400" />
                </button>
              </div>
              <SetupForm onSetup={(data) => { updateSection('investing', data); setShowSetup(false); }} />
            </div>
          </div>
        )}
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-slate-800 border border-slate-700 rounded-2xl flex items-center justify-center mb-5 text-3xl">📈</div>
          <h3 className="text-lg font-semibold text-slate-200 mb-2">No investment portfolio yet</h3>
          <p className="text-sm text-slate-400 max-w-xs leading-relaxed mb-6">
            Set up your portfolio to track performance, asset allocation, and project growth with Monte Carlo simulations.
          </p>
          <button
            onClick={() => setShowSetup(true)}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors shadow-lg shadow-blue-900/30"
          >
            Set Up Portfolio
          </button>
        </div>
      </div>
    );
  }

  // ── Apply type filter ──────────────────────────────────────────────────────
  const filteredManualAccounts = filterType === 'all'
    ? manualInvestmentAccounts
    : manualInvestmentAccounts.filter(a => a.type === filterType);

  const allHoldings = filterType === 'all'
    ? allHoldingsUnfiltered
    : allHoldingsUnfiltered.filter(h => h.accountType === filterType);

  const allocation = buildAllocation(allHoldings);

  // ── Compute all stats purely from real holdings + live prices ───────────────
  // Total portfolio = sum of (shares × live price) for all holdings;
  // fall back to stored manual_balance for accounts with no holdings.
  const manualHoldingsValue = allHoldings
    .filter(h => h.isManual)
    .reduce((sum, h) => sum + (h.value || 0), 0);

  const manualBalanceOnlyValue = filteredManualAccounts
    .filter(a => !a.holdings || a.holdings.length === 0)
    .reduce((sum, a) => sum + (a.balance || 0), 0);

  const totalValue = manualHoldingsValue + manualBalanceOnlyValue;

  // Today's gain = shares × (current price − previous close) per holding
  const todayGain = allHoldings
    .filter(h => h.isManual)
    .reduce((sum, h) => {
      const ticker = (h.ticker || '').toUpperCase();
      const q = liveQuotes[ticker];
      if (!q?.price || !q?.prevClose) return sum;
      return sum + h.shares * (q.price - q.prevClose);
    }, 0);
  const hasTodayGain = allHoldings.some(h => {
    const q = liveQuotes[(h.ticker || '').toUpperCase()];
    return q?.price && q?.prevClose;
  });

  // Total return = sum of (current value − cost basis) for holdings with cost basis
  const holdingsWithCB = allHoldings.filter(h => h.costBasisTotal != null && h.costBasisTotal > 0);
  const combinedTotalReturn = holdingsWithCB.reduce((sum, h) => sum + (h.value - h.costBasisTotal), 0);
  const hasCostBasis = holdingsWithCB.length > 0;

  const VIEWS = [
    { id: 'overview',   label: 'Overview'        },
    { id: 'holdings',   label: 'Holdings'        },
    { id: 'allocation', label: 'Allocation'      },
    { id: 'simulator',  label: 'Simulator'       },
    { id: 'risk',       label: '🧭 Risk Profile'  },
    { id: 'riskstats',  label: '📊 Risk Stats'   },
  ];

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Investing</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {manualInvestmentAccounts.length > 0
              ? `${manualInvestmentAccounts.length} account${manualInvestmentAccounts.length !== 1 ? 's' : ''} · ${totalValue > 0 ? fmt(totalValue) + ' total portfolio' : 'prices loading…'}`
              : 'No accounts yet — add one below'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-900/20 border border-emerald-700/30 px-3 py-1.5 rounded-lg">
          <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
          {priceDate ? `Prices as of ${priceDate}` : 'Live data · Synced today'}
        </div>
      </div>

      {/* Account-type filter bar — only shown when user has multiple types or any manual accounts */}
      {accountTypesPresent.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 font-medium">View:</span>
          {[{ id: 'all', label: 'All Accounts', icon: '📊' }, ...accountTypesPresent].map(opt => (
            <button key={opt.id} onClick={() => setFilterType(opt.id)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors border ${
                filterType === opt.id
                  ? 'bg-blue-600 border-blue-500 text-white'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
              }`}
            >
              {opt.icon && <span>{opt.icon}</span>}
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab nav */}
      <div className="flex gap-1 bg-slate-800 border border-slate-700 rounded-xl p-1 w-fit">
        {VIEWS.map(v => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              view === v.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/30' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* View content */}
      {view === 'overview'   && <OverviewView   allHoldings={allHoldings} allocation={allocation} totalValue={totalValue} investingData={investingData} manualInvestmentAccounts={filteredManualAccounts} combinedTotalReturn={combinedTotalReturn} hasCostBasis={hasCostBasis} todayGain={todayGain} hasTodayGain={hasTodayGain} livePrices={livePrices} />}
      {view === 'holdings'   && <HoldingsView   allHoldings={allHoldings} totalValue={totalValue} investingData={investingData} priceDate={priceDate} livePrices={livePrices} />}
      {view === 'allocation' && <AllocationView allocation={allocation}   totalValue={totalValue} investingData={investingData} />}
      {view === 'simulator'  && <SimulatorView  totalValue={totalValue} allocation={allocation} />}
      {view === 'risk'       && <RiskProfileView totalValue={totalValue} allocation={allocation} />}
      {view === 'riskstats'  && <RiskStatsView  allocation={allocation} totalValue={totalValue} />}
    </div>
  );
}
